import React, { useEffect, useMemo, useState } from 'react';
import Header from '../../components/Header';
import { useAuth } from '../../contexts/AuthContext';
import { useEntity } from '../../contexts/EntityContext';
import { getDailyForYear, getYearRange, getDailyRange, bulkUpsertDaily, deleteDaily, upsertDaily, getDailyByDate, getGoals, upsertGoal, getSettings, upsertSettings, getTargets, upsertTarget } from '../../services/healthService';
import { buildSeries, classifyRate } from '../../services/healthMetrics';
import { computeSMA, computeEWMA, computeRollingMedian, computeLOWESS, resampleLinearDaily, diffOver } from '../../services/seriesUtils';
import { HEALTH_CONFIG } from '../../config/healthConfig';
import './HealthDashboardPage.css';
import { useTheme } from '../../contexts/ThemeContext';
import { businessDateChile06 } from '../../services/timeChile';

function toSeries(dailies, key) {
  const out = [];
  for (const d of (dailies || [])) {
    const raw = d?.[key];
    const num = Number(raw);
    if (raw == null || raw === '' || !Number.isFinite(num) || num === 0) continue;
    out.push({ id: d.id, value: num });
  }
  return out;
}

function formatDateShort(iso) {
  if (!iso) return '';
  const [y, m, d] = String(iso).split('-').map(Number);
  const dateUtc = new Date(Date.UTC(y || 1970, (m || 1) - 1, d || 1));
  return new Intl.DateTimeFormat('es-CL', { month: 'short', day: '2-digit', timeZone: 'UTC' }).format(dateUtc);
}

// (rollingStd/rollingMad moved inside computeUncertaintyBand to avoid unused warnings)
function computeUncertaintyBand(raw, trend, windowSize, scale, method) {
  const t = Array.isArray(trend) ? trend : [];
  if (!t.length) return { upper: [], lower: [] };
  const rMap = new Map((raw || []).map(p => [String(p.id), Number(p.value || 0)]));
  // Build residuals aligned to trend ids; null when no raw
  const residuals = t.map(p => {
    const id = String(p.id || '');
    return rMap.has(id) ? (rMap.get(id) - Number(p.value || 0)) : null;
  });
  // Rolling stats ignoring nulls
  const rollStd = (arr, end, win) => {
    const from = Math.max(0, end - win + 1);
    let n = 0, sum = 0, sum2 = 0;
    for (let i = from; i <= end; i++) {
      const v = arr[i];
      if (v == null || !Number.isFinite(v)) continue;
      n++; sum += v; sum2 += v * v;
    }
    if (n === 0) return 0;
    const mean = sum / n;
    const variance = Math.max(0, (sum2 / n) - mean * mean);
    return Math.sqrt(variance);
  };
  const rollMad = (arr, end, win) => {
    const from = Math.max(0, end - win + 1);
    const slice = [];
    for (let i = from; i <= end; i++) {
      const v = arr[i];
      if (v == null || !Number.isFinite(v)) continue;
      slice.push(Math.abs(v));
    }
    if (!slice.length) return 0;
    slice.sort((a, b) => a - b);
    const mid = Math.floor(slice.length / 2);
    const med = slice.length % 2 === 0 ? (slice[mid - 1] + slice[mid]) / 2 : slice[mid];
    return 1.4826 * med;
  };
  const upper = new Array(t.length);
  const lower = new Array(t.length);
  for (let i = 0; i < t.length; i++) {
    const s = method === 'STD' ? rollStd(residuals, i, windowSize) : rollMad(residuals, i, windowSize);
    const tv = Number(t[i].value || 0);
    const m = Number(scale || 1.5) * s;
    upper[i] = { id: t[i].id, value: tv + m };
    lower[i] = { id: t[i].id, value: tv - m };
  }
  return { upper, lower };
}


function HealthOverlayLineChart({ seriesList, height = 200, valueSuffix = '', bands = [], hoverId = null, onHoverIdChange = null, interactiveLegend = false, renderTooltip = null, yIncludeZero = false, yTickStep = null }) {
  const [hover, setHover] = React.useState(null); // { i, x, id }
  const containerRef = React.useRef(null);
  const [containerWidth, setContainerWidth] = React.useState(0);
  const clipIdRef = React.useRef(`clip-${Math.random().toString(36).slice(2)}`);
  const [hidden, setHidden] = React.useState(() => new Set()); // indexes hidden
  React.useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const cw = entry.contentRect?.width || 0;
        if (cw && cw !== containerWidth) setContainerWidth(cw);
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [containerWidth]);
  const visibleSeriesList = useMemo(() => seriesList.map((s, idx) => hidden.has(idx) ? { ...s, series: [] } : s), [seriesList, hidden]);
  const all = (visibleSeriesList || []).flatMap(s => s.series || []).concat(
    (bands || []).flatMap(b => (b.upper || [])).concat((bands || []).flatMap(b => (b.lower || [])))
  );
  const isEmpty = !all || all.length < 2;
  // Determine length safely even when there is no data
  // Build canonical axis IDs (sorted union of all ids across series and bands)
  const axisIds = useMemo(() => {
    const set = new Set();
    for (const s of visibleSeriesList) for (const p of (s.series || [])) if (p && p.id) set.add(String(p.id));
    for (const b of (bands || [])) {
      for (const p of (b.upper || [])) if (p && p.id) set.add(String(p.id));
      for (const p of (b.lower || [])) if (p && p.id) set.add(String(p.id));
    }
    const arr = Array.from(set);
    arr.sort((a, b) => String(a).localeCompare(String(b)));
    return arr.length >= 2 ? arr : arr;
  }, [visibleSeriesList, bands]);
  const idIndex = useMemo(() => {
    const m = new Map();
    for (let i = 0; i < axisIds.length; i++) m.set(axisIds[i], i);
    return m;
  }, [axisIds]);
  let n = Math.max(2, axisIds.length);
  // Use a virtual width for the viewBox to ensure correct rendering even before measurement,
  // while the actual SVG width stays at 100% to avoid overflow.
  const width = Math.max(360, containerWidth || 360);
  // Dynamic paddings to ensure axis labels fit inside the SVG viewBox
  const padTop = 28;
  const padRight = 28;
  const padBottom = 28;
  const ys = all.map(p => Number(p.value || 0));
  let minY = Math.min(...ys);
  let maxY = Math.max(...ys);
  if (yIncludeZero) {
    minY = Math.min(minY, 0);
    maxY = Math.max(maxY, 0);
  }
  const yPadBase = (maxY - minY) * 0.05;
  let yMin = minY - yPadBase;
  let yMax = maxY + yPadBase;
  // If tick step provided, snap yMin/yMax to step multiples
  if (yTickStep && Number(yTickStep) > 0) {
    const step = Number(yTickStep);
    const floorTo = (v, s) => Math.floor(v / s) * s;
    const ceilTo = (v, s) => Math.ceil(v / s) * s;
    yMin = floorTo(yMin, step);
    yMax = ceilTo(yMax, step);
    if (yMin === yMax) yMax = yMin + step; // ensure range
  }
  // Axis label formatter
  const fmtAxis = (v) => {
    if (Math.abs(v) < 1e-9) return '0';
    const abs = Math.abs(v);
    const prec = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
    return new Intl.NumberFormat('es-CL', { minimumFractionDigits: prec, maximumFractionDigits: prec }).format(v);
  };
  // Estimate needed space for Y labels (simple character-based estimate)
  const yLabelSamples = [fmtAxis(yMin), fmtAxis(yMax)];
  const yLabelMaxLen = yLabelSamples.reduce((m, s) => Math.max(m, String(s || '').length), 0);
  const approxCharW = 7; // px at fontSize 10
  const padLeft = Math.max(28, 14 + yLabelMaxLen * approxCharW);
  const innerW = width - padLeft - padRight;
  const innerH = height - padTop - padBottom;
  const sx = i => padLeft + (i * innerW) / Math.max(1, (n - 1));
  const sy = v => padTop + innerH * (1 - (v - yMin) / Math.max(1e-6, (yMax - yMin)));
  const linePaths = [];
  const pointNodes = [];
  visibleSeriesList.forEach(({ series, color, render, pointRadius, pointOpacity }, idx) => {
    const type = render === 'points' ? 'points' : 'line';
    if (type === 'line') {
      const sorted = (series || []).slice().sort((a, b) => String(a.id).localeCompare(String(b.id)));
      let first = true;
      const d = sorted.map(s => {
        const i = idIndex.get(String(s.id));
        if (i == null) return '';
        const cmd = first ? (first = false, 'M') : 'L';
        return `${cmd} ${sx(i)} ${sy(Number(s.value || 0))}`;
      }).filter(Boolean).join(' ');
      linePaths.push(<path key={`ln-${idx}`} d={d} stroke={color} fill="none" strokeWidth="2" vectorEffect="non-scaling-stroke"/>);
    } else {
      const r = Number(pointRadius || 3);
      const op = (pointOpacity == null ? 0.6 : Number(pointOpacity));
      const nodes = (series || []).map((s, k) => {
        const i = idIndex.get(String(s.id));
        if (i == null) return null;
        return <circle key={`pt-${idx}-${k}`} cx={sx(i)} cy={sy(Number(s.value || 0))} r={r} fill={color} opacity={op} />;
      });
      pointNodes.push(<g key={`pts-${idx}`}>{nodes}</g>);
    }
  });
  const bandPaths = (bands || []).map((b, bidx) => {
    const upper = b.upper || [];
    const lower = b.lower || [];
    if (!upper.length || !lower.length || axisIds.length < 2) return null;
    const uMap = new Map(upper.map(p => [String(p.id), Number(p.value || 0)]));
    const lMap = new Map(lower.map(p => [String(p.id), Number(p.value || 0)]));
    const idxs = [];
    for (let i = 0; i < axisIds.length; i++) {
      const id = axisIds[i];
      if (uMap.has(id) && lMap.has(id)) idxs.push(i);
    }
    if (idxs.length < 2) return null;
    const top = idxs.map(i => `L ${sx(i)} ${sy(uMap.get(axisIds[i]))}`).join(' ').replace(/^L/, 'M');
    const bottom = idxs.slice().reverse().map(i => `L ${sx(i)} ${sy(lMap.get(axisIds[i]))}`).join(' ');
    const d = `${top} ${bottom} Z`;
    return <path key={`band-${bidx}`} d={d} fill={b.color || 'rgba(0,0,0,0.08)'} opacity={b.opacity ?? 0.25} stroke="none" vectorEffect="non-scaling-stroke"/>;
  });
  let yTicks;
  if (yTickStep && Number(yTickStep) > 0) {
    const step = Number(yTickStep);
    const ticks = [];
    for (let v = yMin; v <= yMax + 1e-9; v += step) ticks.push({ y: sy(v), v });
    yTicks = ticks;
  } else {
    const yTickCount = 5;
    yTicks = Array.from({ length: yTickCount }, (_, k) => {
      const t = k / (yTickCount - 1);
      const v = yMin + (yMax - yMin) * t;
      return { y: sy(v), v };
    });
  }
  // Month ticks: first occurrence of each month in the series (aligns to actual data point)
  const monthTicks = (() => {
    const ticks = [];
    let lastYm = '';
    for (let i = 0; i < axisIds.length; i++) {
      const id = String(axisIds[i] || '');
      const ym = id.slice(0, 7);
      if (ym && ym !== lastYm) {
        ticks.push({ x: sx(i), ym });
        lastYm = ym;
      }
    }
    return ticks;
  })();
  const nearestIndexById = (series, id) => {
    if (!series || series.length === 0 || !id) return 0;
    // binary search on lexicographic ids (yyyy-mm-dd)
    let lo = 0, hi = series.length - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const cmp = String(series[mid].id).localeCompare(String(id));
      if (cmp === 0) return mid;
      if (cmp < 0) lo = mid + 1; else hi = mid - 1;
    }
    // lo is first greater; nearest is min(lo, len-1) vs hi
    const cand1 = Math.max(0, Math.min(series.length - 1, lo));
    const cand2 = Math.max(0, Math.min(series.length - 1, hi));
    // choose by absolute diff of date strings (lexicographic distance proxy)
    const d1 = Math.abs(String(series[cand1].id).localeCompare(String(id)));
    const d2 = Math.abs(String(series[cand2].id).localeCompare(String(id)));
    return d1 <= d2 ? cand1 : cand2;
  };
  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const t = Math.max(0, Math.min(1, (mx - padLeft) / innerW));
    const i = Math.round(t * Math.max(0, (n - 1)));
    const id = axisIds[i] || null;
    setHover({ i, x: sx(i), id });
    if (id && onHoverIdChange) onHoverIdChange(id);
  };
  const onLeave = () => {
    setHover(null);
    if (onHoverIdChange) onHoverIdChange(null);
  };
  // external hover sync
  const nearestAxisIndex = React.useCallback((id) => {
    if (!id || axisIds.length === 0) return 0;
    // binary search on axisIds
    let lo = 0, hi = axisIds.length - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const cmp = String(axisIds[mid]).localeCompare(String(id));
      if (cmp === 0) return mid;
      if (cmp < 0) lo = mid + 1; else hi = mid - 1;
    }
    const cand1 = Math.max(0, Math.min(axisIds.length - 1, lo));
    const cand2 = Math.max(0, Math.min(axisIds.length - 1, hi));
    const d1 = Math.abs(String(axisIds[cand1]).localeCompare(String(id)));
    const d2 = Math.abs(String(axisIds[cand2]).localeCompare(String(id)));
    return d1 <= d2 ? cand1 : cand2;
  }, [axisIds]);
  React.useEffect(() => {
    if (!hoverId) { setHover(null); return; }
    const i = nearestAxisIndex(hoverId);
    const x = padLeft + (i * innerW) / Math.max(1, (n - 1));
    setHover({ i, x, id: hoverId });
  }, [hoverId, axisIds, innerW, padLeft, n, nearestAxisIndex]);
  const toggleLegend = (idx) => {
    setHidden(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };
  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', overflow: 'hidden' }}>
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} onMouseMove={onMove} onMouseLeave={onLeave}>
        <defs>
          <clipPath id={clipIdRef.current}>
            <rect x={padLeft} y={padTop} width={innerW} height={innerH} rx="0" ry="0" />
          </clipPath>
        </defs>
        <line x1={padLeft} y1={height - padBottom} x2={width - padRight} y2={height - padBottom} stroke="rgba(0,0,0,.5)" />
        <line x1={padLeft} y1={padTop} x2={padLeft} y2={height - padBottom} stroke="rgba(0,0,0,.5)" />
        {yTicks.map((t, idx) => (
          <g key={`y-${idx}`}>
            <line x1={padLeft} y1={t.y} x2={width - padRight} y2={t.y} stroke="rgba(0,0,0,.1)" />
            <text x={padLeft - 8} y={t.y + 4} textAnchor="end" style={{ fontSize: 10, fill: 'rgba(0,0,0,.7)' }}>{fmtAxis(t.v)}</text>
          </g>
        ))}
        {monthTicks.map((t, idx) => (
          <g key={`mx-${idx}`}>
            <line x1={t.x} y1={padTop} x2={t.x} y2={height - padBottom} stroke="rgba(0,0,0,.08)" />
            <text x={t.x} y={height - padBottom + 16} textAnchor="middle" style={{ fontSize: 10, fill: 'rgba(0,0,0,.7)' }}>{new Date(t.ym + '-01').toLocaleDateString('es-CL', { month: 'short', timeZone: 'UTC' })}</text>
          </g>
        ))}
        <g clipPath={`url(#${clipIdRef.current})`}>
          {!isEmpty && bandPaths}
          {!isEmpty && linePaths}
          {!isEmpty && pointNodes}
          {hover && (
            <line x1={hover.x} y1={padTop} x2={hover.x} y2={height - padBottom} stroke="rgba(0,0,0,.2)" />
          )}
        </g>
      </svg>
      {hover && (
        (() => {
          const boxWidth = 260;
          const left = Math.min(Math.max(hover.x + 8, padLeft + 4), width - padRight - boxWidth);
          return (
            <div className="chart-tooltip" style={{ position: 'absolute', left, top: 8, background: 'rgba(0,0,0,.85)', color: '#fff', padding: '8px 10px', borderRadius: 6, fontSize: 12, maxWidth: boxWidth }}>
              {typeof renderTooltip === 'function'
                ? renderTooltip({ id: hover.id, nearestIndexById, seriesList: visibleSeriesList, valueSuffix })
                : visibleSeriesList.map(({ series, name, color }, idx) => {
                    const j = nearestIndexById(series || [], hover.id);
                    const point = (series || [])[j];
                    if (!point) return null;
                    return (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 8, height: 8, background: color, display: 'inline-block', borderRadius: 2 }} />
                        <span>{name}:</span>
                        <strong>{new Intl.NumberFormat('es-CL', { maximumFractionDigits: 2 }).format(Number(point.value || 0))}{valueSuffix ? ' ' + valueSuffix : ''}</strong>
                        <span style={{ opacity: .8 }}>&nbsp;{formatDateShort(point.id)}</span>
                      </div>
                    );
                  })}
            </div>
          );
        })()
      )}
      {interactiveLegend && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 6 }}>
          {seriesList.map((s, idx) => (
            <button key={`lg-${idx}`} className="btn-secondary" style={{ opacity: hidden.has(idx) ? 0.5 : 1 }} onClick={() => toggleLegend(idx)}>
              <span style={{ width: 10, height: 10, background: s.color, display: 'inline-block', marginRight: 6, verticalAlign: 'middle' }} />
              {hidden.has(idx) ? `Mostrar ${s.name}` : `Ocultar ${s.name}`}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function HealthDashboardPage() {
  const { isDark } = useTheme();
  const { currentUser } = useAuth();
  const { currentEntity } = useEntity();
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [dailies, setDailies] = useState([]);
  const [viewDailies, setViewDailies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [trend, setTrend] = useState('LOWESS'); // LOWESS por defecto
  const [lowessSpan, setLowessSpan] = useState(() => Number(localStorage.getItem('health.lowessSpan') || 0.3));
  const [period, setPeriod] = useState('YEAR'); // YEAR | D30 | D60 | D90 | CUSTOM
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [alpha, setAlpha] = useState(() => Number(localStorage.getItem('health.alpha') || HEALTH_CONFIG.EWMA_ALPHA_DEFAULT));
  const [kcalFat, setKcalFat] = useState(() => Number(localStorage.getItem('health.kcalFat') || HEALTH_CONFIG.ENERGY_PER_KG_FAT_KCAL));
  const [kcalLean, setKcalLean] = useState(() => Number(localStorage.getItem('health.kcalLean') || HEALTH_CONFIG.ENERGY_PER_KG_LEAN_KCAL));
  const [leanWarnPct] = useState(() => Number(localStorage.getItem('health.leanWarnPct') || 35));
  const [goal, setGoal] = useState(() => localStorage.getItem('health.goal') || 'cutting');
  const [calorieTarget, setCalorieTarget] = useState(() => Number(localStorage.getItem('health.calorieTarget') || 2500));
  const [goals, setGoals] = useState([]);
  const [calTargets, setCalTargets] = useState([]); // versioned calorie targets from health_targets
  // Visual settings
  const [showBands, setShowBands] = useState(() => {
    const stored = localStorage.getItem('health.showBands');
    return stored ? stored === '1' : true; // activar por defecto
  });
  const [bandWindow, setBandWindow] = useState(() => Number(localStorage.getItem('health.bandWindow') || 21));
  const [bandScale, setBandScale] = useState(() => Number(localStorage.getItem('health.bandScale') || 1.5));
  const [bandMethod, setBandMethod] = useState(() => localStorage.getItem('health.bandMethod') || 'MAD'); // MAD | STD
  const [calorieView, setCalorieView] = useState(() => localStorage.getItem('health.calorieView') || 'daily'); // daily | weekly
  const [weightTarget, setWeightTarget] = useState(() => Number(localStorage.getItem('health.weightTarget') || 0));
  const [bfTargetMin, setBfTargetMin] = useState(() => Number(localStorage.getItem('health.bfTargetMin') || 0));
  const [bfTargetMax, setBfTargetMax] = useState(() => Number(localStorage.getItem('health.bfTargetMax') || 0));
  const [sharedHoverIndex, setSharedHoverIndex] = useState(null);
  // Composition index settings
  const [compView, setCompView] = useState(() => localStorage.getItem('health.compView') || 'levels'); // 'levels' | 'index'
  const [compDiffMode, setCompDiffMode] = useState(() => localStorage.getItem('health.compDiffMode') || 'trend'); // 'spot' | 'trend'
  const [compBaseId, setCompBaseId] = useState(() => localStorage.getItem('health.compBaseId') || '');

  useEffect(() => {
    async function init() {
      if (!currentUser || !currentEntity) return;
      try {
        setLoading(true); setError('');
        const { years } = await getYearRange(currentUser.uid, currentEntity.id);
        const initYear = years && years.length ? years[0] : new Date().getFullYear();
        setAvailableYears(years || []);
        setSelectedYear(initYear);
        // Load goals (e.g., calorieTarget) for this entity
        const gs = await getGoals(currentUser.uid, currentEntity.id, 'calorieTarget');
        setGoals(gs || []);
        if (gs && gs.length) {
          const last = gs[gs.length - 1];
          if (last && Number.isFinite(Number(last.value))) setCalorieTarget(Number(last.value));
        }
        // Load cross-device view settings
        const vs = await getSettings(currentUser.uid, currentEntity.id);
        // Load versioned calorie targets from health_targets
        const ts = await getTargets(currentUser.uid, currentEntity.id, 'calorieTarget');
        setCalTargets(ts || []);
        if ((!gs || !gs.length) && ts && ts.length) {
          const lastT = ts[ts.length - 1];
          if (lastT && Number.isFinite(Number(lastT.value))) setCalorieTarget(Number(lastT.value));
        }
        if (vs) {
          if (vs.trend) setTrend(vs.trend);
          if (Number.isFinite(Number(vs.lowessSpan))) setLowessSpan(Number(vs.lowessSpan));
          if (Number.isFinite(Number(vs.alpha))) setAlpha(Number(vs.alpha));
          if (vs.showBands != null) setShowBands(Boolean(vs.showBands));
          if (vs.bandMethod) setBandMethod(vs.bandMethod);
          if (Number.isFinite(Number(vs.bandWindow))) setBandWindow(Number(vs.bandWindow));
          if (Number.isFinite(Number(vs.bandScale))) setBandScale(Number(vs.bandScale));
          if (vs.calorieView) setCalorieView(vs.calorieView);
          if (vs.compView) setCompView(vs.compView);
          if (vs.compDiffMode) setCompDiffMode(vs.compDiffMode);
          if (vs.compBaseId) setCompBaseId(vs.compBaseId);
        }
      } catch (e) {
        setError(e.message || 'Error cargando años');
      } finally {
        setLoading(false);
      }
    }
    init();
  }, [currentUser, currentEntity]);

  useEffect(() => {
    let cancelled = false;
    async function loadYear() {
      if (!currentUser || !currentEntity || !selectedYear) return;
      try {
        setLoading(true); setError('');
        const list = await getDailyForYear(currentUser.uid, currentEntity.id, selectedYear);
        if (!cancelled) setDailies(list || []);
      } catch (e) { if (!cancelled) setError(e.message || 'Error cargando datos'); }
      finally { if (!cancelled) setLoading(false); }
    }
    loadYear();
    return () => { cancelled = true; };
  }, [selectedYear, currentUser, currentEntity]);

  useEffect(() => {
    let cancelled = false;
    async function applyPeriod() {
      if (!currentUser || !currentEntity) return;
      if (period === 'YEAR') { setViewDailies(dailies || []); return; }
      if (period === 'CUSTOM') {
        if (!customStart || !customEnd) { setViewDailies([]); return; }
        try {
          setLoading(true);
          const list = await getDailyRange(currentUser.uid, currentEntity.id, customStart, customEnd);
          if (!cancelled) setViewDailies(list || []);
        } catch (e) { if (!cancelled) setError(e.message || 'Error cargando rango personalizado'); }
        finally { if (!cancelled) setLoading(false); }
        return;
      }
      const endId = (dailies && dailies.length) ? dailies[dailies.length - 1].id : null;
      if (!endId) { setViewDailies([]); return; }
      const days = period === 'D30' ? 30 : period === 'D60' ? 60 : 90;
      const [y, m, d] = endId.split('-').map(Number);
      const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
      dt.setUTCDate(dt.getUTCDate() - days);
      const startId = `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2,'0')}-${String(dt.getUTCDate()).padStart(2,'0')}`;
      try {
        setLoading(true);
        const list = await getDailyRange(currentUser.uid, currentEntity.id, startId, endId);
        if (!cancelled) setViewDailies(list || []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Error cargando rango');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    applyPeriod();
    return () => { cancelled = true; };
  }, [period, dailies, currentUser, currentEntity, customStart, customEnd]);

  const base = useMemo(() => buildSeries(viewDailies), [viewDailies]);
  const weightSMA7 = useMemo(() => computeSMA(resampleLinearDaily(base.weight), 7), [base.weight]);
  const weightSMA14 = useMemo(() => computeSMA(resampleLinearDaily(base.weight), 14), [base.weight]);
  const weightEWMA = useMemo(() => computeEWMA(resampleLinearDaily(base.weight), alpha || 0.3), [base.weight, alpha]);
  const weightMED7 = useMemo(() => computeRollingMedian(resampleLinearDaily(base.weight), 7), [base.weight]);
  const weightLOWESS = useMemo(() => computeLOWESS(resampleLinearDaily(base.weight), lowessSpan || 0.3), [base.weight, lowessSpan]);

  const weightTrend = trend === 'SMA14' ? weightSMA14 : trend === 'EWMA' ? weightEWMA : trend === 'MED7' ? weightMED7 : trend === 'LOWESS' ? weightLOWESS : weightSMA7;

  // Build smoothed fat/lean series following same trend selection
  const fatSMA7 = useMemo(() => computeSMA(resampleLinearDaily(base.fatMass), 7), [base.fatMass]);
  const fatSMA14 = useMemo(() => computeSMA(resampleLinearDaily(base.fatMass), 14), [base.fatMass]);
  const fatEWMA = useMemo(() => computeEWMA(resampleLinearDaily(base.fatMass), alpha || 0.3), [base.fatMass, alpha]);
  const fatMED7 = useMemo(() => computeRollingMedian(resampleLinearDaily(base.fatMass), 7), [base.fatMass]);
  const fatLOWESS = useMemo(() => computeLOWESS(resampleLinearDaily(base.fatMass), lowessSpan || 0.3), [base.fatMass, lowessSpan]);
  const leanSMA7 = useMemo(() => computeSMA(resampleLinearDaily(base.leanMass), 7), [base.leanMass]);
  const leanSMA14 = useMemo(() => computeSMA(resampleLinearDaily(base.leanMass), 14), [base.leanMass]);
  const leanEWMA = useMemo(() => computeEWMA(resampleLinearDaily(base.leanMass), alpha || 0.3), [base.leanMass, alpha]);
  const leanMED7 = useMemo(() => computeRollingMedian(resampleLinearDaily(base.leanMass), 7), [base.leanMass]);
  const leanLOWESS = useMemo(() => computeLOWESS(resampleLinearDaily(base.leanMass), lowessSpan || 0.3), [base.leanMass, lowessSpan]);
  const fatTrend = trend === 'SMA14' ? fatSMA14 : trend === 'EWMA' ? fatEWMA : trend === 'MED7' ? fatMED7 : trend === 'LOWESS' ? fatLOWESS : fatSMA7;
  const leanTrend = trend === 'SMA14' ? leanSMA14 : trend === 'EWMA' ? leanEWMA : trend === 'MED7' ? leanMED7 : trend === 'LOWESS' ? leanLOWESS : leanSMA7;

  // Daily LOWESS deltas (grams) for weight and fat
  const weightLowessDailyG = useMemo(() => {
    const w = computeLOWESS(resampleLinearDaily(base.weight), lowessSpan || 0.3);
    const d = diffOver(w, 1).map(p => ({ id: p.id, value: Number(p.value || 0) * 1000 }));
    return d;
  }, [base.weight, lowessSpan]);
  const fatLowessDailyG = useMemo(() => {
    const f = computeLOWESS(resampleLinearDaily(base.fatMass), lowessSpan || 0.3);
    const d = diffOver(f, 1).map(p => ({ id: p.id, value: Number(p.value || 0) * 1000 }));
    return d;
  }, [base.fatMass, lowessSpan]);

  // Rolling 7-day OLS: dFat ~ dWeight -> slope (g fat / g weight), CI95
  const rollingFatRate = useMemo(() => {
    const xMap = new Map((weightLowessDailyG || []).map(p => [String(p.id), Number(p.value || 0)]));
    const yMap = new Map((fatLowessDailyG || []).map(p => [String(p.id), Number(p.value || 0)]));
    const ids = Array.from(xMap.keys()).filter(id => yMap.has(id)).sort((a, b) => String(a).localeCompare(String(b)));
    const out = [];
    const win = 7;
    // helper: OLS slope and CI (simple unweighted)
    const ols = (xs, ys) => {
      const n = xs.length;
      if (n < 2) return { b: 0, se: 0 };
      let sx = 0, sy = 0, sxx = 0, sxy = 0;
      for (let i = 0; i < n; i++) { const x = xs[i], y = ys[i]; sx += x; sy += y; sxx += x * x; sxy += x * y; }
      const xbar = sx / n, ybar = sy / n;
      const ssxx = sxx - n * xbar * xbar;
      const ssxy = sxy - n * xbar * ybar;
      const b = Math.abs(ssxx) > 1e-12 ? ssxy / ssxx : 0;
      // residual variance and SE(b)
      let sse = 0;
      for (let i = 0; i < n; i++) { const yhat = b * xs[i] + (ybar - b * xbar); sse += (ys[i] - yhat) ** 2; }
      const sigma2 = n > 2 ? sse / (n - 2) : 0;
      const se = Math.abs(ssxx) > 1e-12 ? Math.sqrt(sigma2 / ssxx) : 0;
      return { b, se };
    };
    for (let i = 0; i < ids.length; i++) {
      const endId = ids[i];
      const startIdx = Math.max(0, i - win + 1);
      const slice = ids.slice(startIdx, i + 1);
      const xs = slice.map(id => xMap.get(id));
      const ys = slice.map(id => yMap.get(id));
      if (xs.length < 3) continue;
      const { b, se } = ols(xs, ys);
      // 95% CI using normal approx (ok for n>=7)
      const z = 1.96;
      const lo = b - z * se;
      const hi = b + z * se;
      // Convert to g/kg for interpretability: g fat per 1 kg weight change
      const value = b * 1000; // since x is g and 1 kg = 1000 g; to express per kg, multiply slope by 1000
      const loKg = lo * 1000;
      const hiKg = hi * 1000;
      out.push({ id: endId, value, lo: loKg, hi: hiKg });
    }
    return out;
  }, [weightLowessDailyG, fatLowessDailyG]);

  const fatRateSeries = useMemo(() => rollingFatRate.map(p => ({ id: p.id, value: p.value })), [rollingFatRate]);
  const fatRateBand = useMemo(() => ({ upper: rollingFatRate.map(p => ({ id: p.id, value: p.hi })), lower: rollingFatRate.map(p => ({ id: p.id, value: p.lo })) }), [rollingFatRate]);

  // Signed deltas vs 7d/14d atrás (alineado con lógica del gráfico)
  const deltaOverDays = (series, days) => {
    if (!series || series.length === 0) return 0;
    const n = series.length;
    if (n <= days) return 0;
    return Number(series[n - 1].value || 0) - Number(series[n - 1 - days].value || 0);
  };

  const wDelta7 = useMemo(() => deltaOverDays(weightTrend, 7), [weightTrend]);
  const wDelta14 = useMemo(() => deltaOverDays(weightTrend, 14), [weightTrend]);
  const wDelta21 = useMemo(() => deltaOverDays(weightTrend, 21), [weightTrend]);
  const wDelta28 = useMemo(() => deltaOverDays(weightTrend, 28), [weightTrend]);
  const fDelta7 = useMemo(() => deltaOverDays(fatTrend, 7), [fatTrend]);
  const fDelta14 = useMemo(() => deltaOverDays(fatTrend, 14), [fatTrend]);
  const fDelta21 = useMemo(() => deltaOverDays(fatTrend, 21), [fatTrend]);
  const fDelta28 = useMemo(() => deltaOverDays(fatTrend, 28), [fatTrend]);
  const lDelta7 = useMemo(() => deltaOverDays(leanTrend, 7), [leanTrend]);
  const lDelta14 = useMemo(() => deltaOverDays(leanTrend, 14), [leanTrend]);
  const lDelta21 = useMemo(() => deltaOverDays(leanTrend, 21), [leanTrend]);
  const lDelta28 = useMemo(() => deltaOverDays(leanTrend, 28), [leanTrend]);

  const shares7 = useMemo(() => {
    const total = wDelta7;
    let compA = 0, compB = 0;
    if (total < -1e-6) { // pérdida
      compA = Math.max(0, -fDelta7);
      compB = Math.max(0, -lDelta7);
    } else if (total > 1e-6) { // ganancia
      compA = Math.max(0, fDelta7);
      compB = Math.max(0, lDelta7);
    } else {
      return { fatPct: 0, leanPct: 0 };
    }
    const sum = compA + compB;
    if (sum <= 0) return { fatPct: 0, leanPct: 0 };
    return { fatPct: (compA / sum) * 100, leanPct: (compB / sum) * 100 };
  }, [wDelta7, fDelta7, lDelta7]);
  const shares14 = useMemo(() => {
    const total = wDelta14;
    let compA = 0, compB = 0;
    if (total < -1e-6) {
      compA = Math.max(0, -fDelta14);
      compB = Math.max(0, -lDelta14);
    } else if (total > 1e-6) {
      compA = Math.max(0, fDelta14);
      compB = Math.max(0, lDelta14);
    } else {
      return { fatPct: 0, leanPct: 0 };
    }
    const sum = compA + compB;
    if (sum <= 0) return { fatPct: 0, leanPct: 0 };
    return { fatPct: (compA / sum) * 100, leanPct: (compB / sum) * 100 };
  }, [wDelta14, fDelta14, lDelta14]);
  const shares21 = useMemo(() => {
    const total = wDelta21;
    let compA = 0, compB = 0;
    if (total < -1e-6) {
      compA = Math.max(0, -fDelta21);
      compB = Math.max(0, -lDelta21);
    } else if (total > 1e-6) {
      compA = Math.max(0, fDelta21);
      compB = Math.max(0, lDelta21);
    } else {
      return { fatPct: 0, leanPct: 0 };
    }
    const sum = compA + compB;
    if (sum <= 0) return { fatPct: 0, leanPct: 0 };
    return { fatPct: (compA / sum) * 100, leanPct: (compB / sum) * 100 };
  }, [wDelta21, fDelta21, lDelta21]);
  const shares28 = useMemo(() => {
    const total = wDelta28;
    let compA = 0, compB = 0;
    if (total < -1e-6) {
      compA = Math.max(0, -fDelta28);
      compB = Math.max(0, -lDelta28);
    } else if (total > 1e-6) {
      compA = Math.max(0, fDelta28);
      compB = Math.max(0, lDelta28);
    } else {
      return { fatPct: 0, leanPct: 0 };
    }
    const sum = compA + compB;
    if (sum <= 0) return { fatPct: 0, leanPct: 0 };
    return { fatPct: (compA / sum) * 100, leanPct: (compB / sum) * 100 };
  }, [wDelta28, fDelta28, lDelta28]);

  // old deficit7/14 removed (not used anymore)

  // Weekly energy balance from body stores change vs 7 días antes: positivo = superávit, negativo = déficit
  const dailyEnergyBalance = useMemo(() => {
    const f = fatTrend || [];
    const l = leanTrend || [];
    if (!f.length || !l.length) return [];
    const idToIndexF = new Map();
    for (let i = 0; i < f.length; i++) idToIndexF.set(f[i].id, i);
    const idToIndexL = new Map();
    for (let i = 0; i < l.length; i++) idToIndexL.set(l[i].id, i);
    const toMinus7 = (id) => {
      const [y, m, d] = String(id).split('-').map(Number);
      const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
      dt.setUTCDate(dt.getUTCDate() - 7);
      const yy = dt.getUTCFullYear();
      const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
      const dd = String(dt.getUTCDate()).padStart(2, '0');
      return `${yy}-${mm}-${dd}`;
    };
    const out = [];
    for (const p of f) {
      const id = p.id;
      if (!idToIndexL.has(id)) continue;
      const prevId = toMinus7(id);
      const iF = idToIndexF.get(id);
      const iL = idToIndexL.get(id);
      const jF = idToIndexF.get(prevId);
      const jL = idToIndexL.get(prevId);
      if (jF == null || jL == null) continue; // requiere contraparte exacta -7d
      const dFat = Number((f[iF]?.value || 0)) - Number((f[jF]?.value || 0));
      const dLean = Number((l[iL]?.value || 0)) - Number((l[jL]?.value || 0));
      const energyChange = dFat * (kcalFat || 0) + dLean * (kcalLean || 0);
      const netDaily = energyChange / 7; // promedio diario
      out.push({ id, value: netDaily });
    }
    return out;
  }, [fatTrend, leanTrend, kcalFat, kcalLean]);

  // Keep adherence logic using positive-only deficit approximation (convert negative balance to positive deficit)
  const dailyDeficit = useMemo(() => (dailyEnergyBalance || []).map(p => ({ id: p.id, value: Math.max(0, -Number(p.value || 0)) })), [dailyEnergyBalance]);

  // Removed unused calDailySeries; adherence now computed via target series elsewhere
  // const [adhOverlay, setAdhOverlay] = useState({ open: false, title: '', items: [] });

  const rate14 = useMemo(() => {
    const w = wDelta14; // kg over 14d (signed)
    return (w / 14) * 7; // kg por semana
  }, [wDelta14]);
  const rateClass = useMemo(() => classifyRate(rate14, goal), [rate14, goal]);

  // Latest net daily energy (7d) from balance series and a 14d counterpart
  const netDaily7 = useMemo(() => {
    const s = dailyEnergyBalance || [];
    return s.length ? Number(s[s.length - 1].value || 0) : 0;
  }, [dailyEnergyBalance]);
  const netDaily14 = useMemo(() => {
    const f = fatTrend || [];
    const l = leanTrend || [];
    if (!f.length || !l.length) return 0;
    const lastId = f[f.length - 1]?.id;
    if (!lastId || l[l.length - 1]?.id !== lastId) return 0;
    const toMinus = (id, days) => {
      const [y, m, d] = String(id).split('-').map(Number);
      const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
      dt.setUTCDate(dt.getUTCDate() - days);
      const yy = dt.getUTCFullYear();
      const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
      const dd = String(dt.getUTCDate()).padStart(2, '0');
      return `${yy}-${mm}-${dd}`;
    };
    const prevId = toMinus(lastId, 14);
    const idxF = f.findIndex(p => p.id === prevId);
    const idxL = l.findIndex(p => p.id === prevId);
    if (idxF < 0 || idxL < 0) return 0;
    const dFat = Number(f[f.length - 1].value || 0) - Number(f[idxF].value || 0);
    const dLean = Number(l[l.length - 1].value || 0) - Number(l[idxL].value || 0);
    const energy = dFat * (kcalFat || 0) + dLean * (kcalLean || 0);
    return energy / 14; // promedio diario
  }, [fatTrend, leanTrend, kcalFat, kcalLean]);
  const netDaily21 = useMemo(() => {
    const f = fatTrend || [];
    const l = leanTrend || [];
    if (!f.length || !l.length) return 0;
    const lastId = f[f.length - 1]?.id;
    if (!lastId || l[l.length - 1]?.id !== lastId) return 0;
    const toMinus = (id, days) => {
      const [y, m, d] = String(id).split('-').map(Number);
      const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
      dt.setUTCDate(dt.getUTCDate() - days);
      const yy = dt.getUTCFullYear();
      const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
      const dd = String(dt.getUTCDate()).padStart(2, '0');
      return `${yy}-${mm}-${dd}`;
    };
    const prevId = toMinus(lastId, 21);
    const idxF = f.findIndex(p => p.id === prevId);
    const idxL = l.findIndex(p => p.id === prevId);
    if (idxF < 0 || idxL < 0) return 0;
    const dFat = Number(f[f.length - 1].value || 0) - Number(f[idxF].value || 0);
    const dLean = Number(l[l.length - 1].value || 0) - Number(l[idxL].value || 0);
    const energy = dFat * (kcalFat || 0) + dLean * (kcalLean || 0);
    return energy / 21;
  }, [fatTrend, leanTrend, kcalFat, kcalLean]);
  const netDaily28 = useMemo(() => {
    const f = fatTrend || [];
    const l = leanTrend || [];
    if (!f.length || !l.length) return 0;
    const lastId = f[f.length - 1]?.id;
    if (!lastId || l[l.length - 1]?.id !== lastId) return 0;
    const toMinus = (id, days) => {
      const [y, m, d] = String(id).split('-').map(Number);
      const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
      dt.setUTCDate(dt.getUTCDate() - days);
      const yy = dt.getUTCFullYear();
      const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
      const dd = String(dt.getUTCDate()).padStart(2, '0');
      return `${yy}-${mm}-${dd}`;
    };
    const prevId = toMinus(lastId, 28);
    const idxF = f.findIndex(p => p.id === prevId);
    const idxL = l.findIndex(p => p.id === prevId);
    if (idxF < 0 || idxL < 0) return 0;
    const dFat = Number(f[f.length - 1].value || 0) - Number(f[idxF].value || 0);
    const dLean = Number(l[l.length - 1].value || 0) - Number(l[idxL].value || 0);
    const energy = dFat * (kcalFat || 0) + dLean * (kcalLean || 0);
    return energy / 28;
  }, [fatTrend, leanTrend, kcalFat, kcalLean]);

  // Trends for body fat pct and calories
  const bfSMA7 = useMemo(() => computeSMA(resampleLinearDaily(base.bodyFatPct), 7), [base.bodyFatPct]);
  const bfSMA14 = useMemo(() => computeSMA(resampleLinearDaily(base.bodyFatPct), 14), [base.bodyFatPct]);
  const bfEWMA = useMemo(() => computeEWMA(resampleLinearDaily(base.bodyFatPct), alpha || 0.3), [base.bodyFatPct, alpha]);
  const bfMED7 = useMemo(() => computeRollingMedian(resampleLinearDaily(base.bodyFatPct), 7), [base.bodyFatPct]);
  const bfLOWESS = useMemo(() => computeLOWESS(resampleLinearDaily(base.bodyFatPct), lowessSpan || 0.3), [base.bodyFatPct, lowessSpan]);
  const bfTrend = trend === 'SMA14' ? bfSMA14 : trend === 'EWMA' ? bfEWMA : trend === 'MED7' ? bfMED7 : trend === 'LOWESS' ? bfLOWESS : bfSMA7;
  const calResampled = useMemo(() => resampleLinearDaily(base.calories), [base.calories]);
  const calSMA7 = useMemo(() => computeSMA(calResampled, 7), [calResampled]);
  const calSMA14 = useMemo(() => computeSMA(calResampled, 14), [calResampled]);
  const calEWMA = useMemo(() => computeEWMA(calResampled, alpha || 0.3), [calResampled, alpha]);
  const calMED7 = useMemo(() => computeRollingMedian(calResampled, 7), [calResampled]);
  const calLOWESS = useMemo(() => computeLOWESS(calResampled, lowessSpan || 0.3), [calResampled, lowessSpan]);
  // Build per-date target series from versioned goals
  const buildTargetSeries = React.useCallback((ids, goalsList, fallback) => {
    const out = [];
    if (!Array.isArray(ids) || ids.length === 0) return out;
    const sorted = [...(goalsList || [])]
      .filter(g => g?.type === 'calorieTarget' && g?.startId)
      .sort((a, b) => String(a.startId).localeCompare(String(b.startId)));
    let k = 0;
    let cur = sorted[0] || null;
    for (const p of ids) {
      const id = String(p.id || '');
      while (k + 1 < sorted.length && String(sorted[k + 1].startId) <= id) {
        k++;
        cur = sorted[k];
      }
      const val = Number((cur && String(cur.startId) <= id) ? cur.value : fallback);
      if (Number.isFinite(val) && val > 0) out.push({ id, value: val });
    }
    return out;
  }, []);
  // Build dynamic per-day calorie targets BEFORE adherence uses it
  const calTargetSeriesDaily = useMemo(() => buildTargetSeries(calLOWESS, (calTargets && calTargets.length ? calTargets : goals), calorieTarget), [buildTargetSeries, calLOWESS, calTargets, goals, calorieTarget]);
  const calTrend = trend === 'SMA14' ? calSMA14 : trend === 'EWMA' ? calEWMA : trend === 'MED7' ? calMED7 : trend === 'LOWESS' ? calLOWESS : calSMA7;

  // Weekly aggregation for calories (average per week)
  const calWeekly = useMemo(() => {
    if (!Array.isArray(base.calories) || base.calories.length === 0) return [];
    const weeks = new Map();
    for (const p of base.calories) {
      const [y, m, d] = String(p.id).split('-').map(Number);
      const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
      // ISO week number
      const dayNum = (dt.getUTCDay() + 6) % 7; // 0=Mon..6=Sun
      const thursday = new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate() - dayNum + 3));
      const firstThursday = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 4));
      const week = 1 + Math.round(((thursday - firstThursday) / 86400000 - 3) / 7);
      const key = `${thursday.getUTCFullYear()}-W${String(week).padStart(2,'0')}`;
      const bucket = weeks.get(key) || { sum: 0, n: 0, lastId: p.id };
      bucket.sum += Number(p.value || 0); bucket.n += 1; bucket.lastId = p.id; weeks.set(key, bucket);
    }
    return Array.from(weeks.values()).map(w => ({ id: w.lastId, value: w.n ? w.sum / w.n : 0 }));
  }, [base.calories]);

  // Latest date id for table ranges
  const lastDateId = useMemo(() => (viewDailies && viewDailies.length ? String(viewDailies[viewDailies.length - 1].id) : ''), [viewDailies]);
  const toMinusDays = (id, days) => {
    if (!id) return '';
    const [y, m, d] = String(id).split('-').map(Number);
    const dt = new Date(Date.UTC(y || 1970, (m || 1) - 1, d || 1));
    dt.setUTCDate(dt.getUTCDate() - Number(days || 0));
    const yy = dt.getUTCFullYear();
    const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(dt.getUTCDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
  };

  // Ensure a default baseId for composition index when data changes
  useEffect(() => {
    const firstId = (viewDailies && viewDailies.length) ? String(viewDailies[0].id) : '';
    if (!compBaseId || (firstId && String(compBaseId) < firstId)) {
      setCompBaseId(firstId);
      if (firstId) localStorage.setItem('health.compBaseId', firstId);
    }
  }, [viewDailies, compBaseId]);

  // Spot series for composition (kg)
  const compSpotFatKg = useMemo(() => {
    const out = [];
    for (const d of (viewDailies || [])) {
      const w = Number(d?.weightKg || 0);
      const bf = Number(d?.bodyFatPct || 0) / 100;
      if (!Number.isFinite(w) || !Number.isFinite(bf) || w === 0) continue;
      const fat = Math.max(0, w * bf);
      out.push({ id: d.id, value: fat });
    }
    return out;
  }, [viewDailies]);
  const compSpotLeanKg = useMemo(() => {
    const out = [];
    for (const d of (viewDailies || [])) {
      const w = Number(d?.weightKg || 0);
      const bf = Number(d?.bodyFatPct || 0) / 100;
      if (!Number.isFinite(w) || !Number.isFinite(bf) || w === 0) continue;
      const fat = Math.max(0, w * bf);
      const lean = Math.max(0, w - fat);
      out.push({ id: d.id, value: lean });
    }
    return out;
  }, [viewDailies]);

  // Helpers to compute Δ vs base in grams (only from base forward)
  const computeDiffFromBaseG = React.useCallback((series, baseId) => {
    if (!Array.isArray(series) || series.length === 0 || !baseId) return [];
    const idx = series.findIndex(p => String(p.id) === String(baseId));
    const baseIndex = idx >= 0 ? idx : 0;
    const baseVal = Number(series[baseIndex]?.value || 0);
    const out = [];
    for (let i = baseIndex; i < series.length; i++) {
      const p = series[i];
      const g = (Number(p.value || 0) - baseVal) * 1000; // grams
      out.push({ id: p.id, value: g });
    }
    return out;
  }, []);

  const fatSrcKg = compDiffMode === 'spot' ? compSpotFatKg : fatTrend;
  const leanSrcKg = compDiffMode === 'spot' ? compSpotLeanKg : leanTrend;
  const compFatDiffG = useMemo(() => computeDiffFromBaseG(fatSrcKg || [], compBaseId), [fatSrcKg, compBaseId, computeDiffFromBaseG]);
  const compLeanDiffG = useMemo(() => computeDiffFromBaseG(leanSrcKg || [], compBaseId), [leanSrcKg, compBaseId, computeDiffFromBaseG]);

  // Area bands for index view: positive and negative for each component
  const compFatPosBand = useMemo(() => ({ upper: (compFatDiffG || []).map(p => ({ id: p.id, value: Math.max(0, Number(p.value || 0)) })), lower: (compFatDiffG || []).map(p => ({ id: p.id, value: 0 })) }), [compFatDiffG]);
  const compFatNegBand = useMemo(() => ({ upper: (compFatDiffG || []).map(p => ({ id: p.id, value: 0 })), lower: (compFatDiffG || []).map(p => ({ id: p.id, value: Math.min(0, Number(p.value || 0)) })) }), [compFatDiffG]);
  const compLeanPosBand = useMemo(() => ({ upper: (compLeanDiffG || []).map(p => ({ id: p.id, value: Math.max(0, Number(p.value || 0)) })), lower: (compLeanDiffG || []).map(p => ({ id: p.id, value: 0 })) }), [compLeanDiffG]);
  const compLeanNegBand = useMemo(() => ({ upper: (compLeanDiffG || []).map(p => ({ id: p.id, value: 0 })), lower: (compLeanDiffG || []).map(p => ({ id: p.id, value: Math.min(0, Number(p.value || 0)) })) }), [compLeanDiffG]);

  // % de la baja de peso atribuible a grasa (solo considera períodos con pérdida neta)
  const compFatLossSharePct = useMemo(() => {
    const fMap = new Map((compFatDiffG || []).map(p => [String(p.id), Number(p.value || 0)]));
    const lMap = new Map((compLeanDiffG || []).map(p => [String(p.id), Number(p.value || 0)]));
    const ids = Array.from(new Set([...(compFatDiffG || []).map(p => String(p.id)), ...(compLeanDiffG || []).map(p => String(p.id))])).sort((a, b) => String(a).localeCompare(String(b)));
    const out = [];
    for (const id of ids) {
      const df = Number(fMap.get(id) || 0);
      const dl = Number(lMap.get(id) || 0);
      const dTotal = df + dl; // Δ total en g
      if (dTotal >= -1e-6) continue; // solo pérdida neta
      const fatLossG = Math.max(0, -df);
      const leanLossG = Math.max(0, -dl);
      const denom = fatLossG + leanLossG;
      if (denom <= 0) continue;
      const pct = (fatLossG / denom) * 100;
      out.push({ id, value: pct });
    }
    return out;
  }, [compFatDiffG, compLeanDiffG]);

  // Latest snapshot KPIs
  const latest = viewDailies && viewDailies.length ? viewDailies[viewDailies.length - 1] : null;
  const latestWeight = Number(latest?.weightKg || 0);
  const latestBf = Number(latest?.bodyFatPct || 0);
  const latestFat = fatTrend && fatTrend.length ? Number(fatTrend[fatTrend.length - 1].value || 0) : 0;
  const latestLean = leanTrend && leanTrend.length ? Number(leanTrend[leanTrend.length - 1].value || 0) : 0;
  // Spot vs Trend helpers for KPI bar (spot from último registro; tendencia según selección)
  const latestWeightTrend = weightTrend && weightTrend.length ? Number(weightTrend[weightTrend.length - 1].value || 0) : 0;
  const latestBfTrend = bfTrend && bfTrend.length ? Number(bfTrend[bfTrend.length - 1].value || 0) : 0;
  const latestSpotFat = Math.max(0, latestWeight * (latestBf / 100));
  const latestSpotLean = Math.max(0, latestWeight - latestSpotFat);
  const trendLabel = useMemo(() => (trend === 'SMA14' ? 'SMA14' : trend === 'EWMA' ? 'EWMA' : trend === 'MED7' ? 'Mediana 7d' : trend === 'LOWESS' ? 'LOWESS' : 'SMA7'), [trend]);
  const leanRisk = shares14.leanPct >= Number(leanWarnPct || 35);
  const [isSettingsOpen, setSettingsOpen] = useState(false);
  const [isEntryOpen, setEntryOpen] = useState(false);
  const [entryTab, setEntryTab] = useState('health'); // 'health' | 'targets'

  // Uncertainty bands for weight and body fat
  const weightBand = useMemo(() => showBands ? computeUncertaintyBand(base.weight, weightTrend, bandWindow, bandScale, bandMethod) : { upper: [], lower: [] }, [showBands, base.weight, weightTrend, bandWindow, bandScale, bandMethod]);
  const bfBand = useMemo(() => showBands ? computeUncertaintyBand(base.bodyFatPct, bfTrend, bandWindow, bandScale, bandMethod) : { upper: [], lower: [] }, [showBands, base.bodyFatPct, bfTrend, bandWindow, bandScale, bandMethod]);
  const calBand = useMemo(() => showBands ? computeUncertaintyBand(base.calories, calTrend, bandWindow, bandScale, bandMethod) : { upper: [], lower: [] }, [showBands, base.calories, calTrend, bandWindow, bandScale, bandMethod]);
  // Calorie intake vs target (7d average) for comparison line in balance chart
  const calLOWESS7 = useMemo(() => computeSMA(calLOWESS, 7), [calLOWESS]);
  const calTarget7 = useMemo(() => computeSMA(calTargetSeriesDaily, 7), [calTargetSeriesDaily]);
  const calIntakeVsTarget7 = useMemo(() => {
    const tMap = new Map((calTarget7 || []).map(p => [String(p.id), Number(p.value || 0)]));
    const out = [];
    for (const p of (calLOWESS7 || [])) {
      const id = String(p.id || '');
      if (!tMap.has(id)) continue;
      const diff = Number(p.value || 0) - tMap.get(id);
      out.push({ id, value: diff });
    }
    return out;
  }, [calLOWESS7, calTarget7]);
  // Bands for energy balance areas (above/below zero)
  const energyPosBand = useMemo(() => {
    const s = dailyEnergyBalance || [];
    return {
      upper: s.map(p => ({ id: p.id, value: Math.max(0, Number(p.value || 0)) })),
      lower: s.map(p => ({ id: p.id, value: 0 })),
    };
  }, [dailyEnergyBalance]);
  const energyNegBand = useMemo(() => {
    const s = dailyEnergyBalance || [];
    return {
      upper: s.map(p => ({ id: p.id, value: 0 })),
      lower: s.map(p => ({ id: p.id, value: Math.min(0, Number(p.value || 0)) })),
    };
  }, [dailyEnergyBalance]);

  // Entry modal state
  const [dateId, setDateId] = useState(businessDateChile06());
  const [weightKg, setWeightKg] = useState('');
  const [bodyFatPct, setBodyFatPct] = useState('');
  const [calories, setCalories] = useState('');
  const [notes, setNotes] = useState('');
  const [chest, setChest] = useState('');
  const [waist, setWaist] = useState('');
  const [hip, setHip] = useState('');
  const [protein, setProtein] = useState('');
  const [carbs, setCarbs] = useState('');
  const [fat, setFat] = useState('');
  const [steps, setSteps] = useState('');
  const [sleepHours, setSleepHours] = useState('');
  const [entryLoading, setEntryLoading] = useState(false);
  const [entryError, setEntryError] = useState('');
  const [entrySaved, setEntrySaved] = useState(false);
  // Targets inputs inside Register modal (initialized from current top-level targets)
  const [tCalorieTarget, setTCalorieTarget] = useState(() => Number(localStorage.getItem('health.calorieTarget') || calorieTarget || 0));
  const [tWeightTarget, setTWeightTarget] = useState(() => Number(localStorage.getItem('health.weightTarget') || weightTarget || 0));
  const [tBfMin, setTBfMin] = useState(() => Number(localStorage.getItem('health.bfTargetMin') || bfTargetMin || 0));
  const [tBfMax, setTBfMax] = useState(() => Number(localStorage.getItem('health.bfTargetMax') || bfTargetMax || 0));

  // Accessibility and UX: close on Escape, lock background scroll
  // Accessibility and UX: close on Escape, lock background scroll when any modal is open
  useEffect(() => {
    if (!isSettingsOpen && !isEntryOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (isEntryOpen) setEntryOpen(false); else if (isSettingsOpen) setSettingsOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prevOverflow; };
  }, [isSettingsOpen, isEntryOpen, calorieTarget, weightTarget, bfTargetMin, bfTargetMax]);

  // Load existing entry when entry modal opens or date changes
  useEffect(() => {
    let cancelled = false;
    async function loadExisting() {
      try {
        setEntryError(''); setEntrySaved(false);
        if (!isEntryOpen || !currentUser || !currentEntity || !dateId) return;
        setEntryLoading(true);
        const d = await getDailyByDate(currentUser.uid, currentEntity.id, dateId);
        if (!cancelled) {
          setWeightKg(d?.weightKg ?? '');
          setBodyFatPct(d?.bodyFatPct ?? '');
          setCalories(d?.calories ?? '');
          setNotes(d?.notes ?? '');
          setChest(d?.circumferences?.chest ?? '');
          setWaist(d?.circumferences?.waist ?? '');
          setHip(d?.circumferences?.hip ?? '');
          setProtein(d?.macros?.protein ?? '');
          setCarbs(d?.macros?.carbs ?? '');
          setFat(d?.macros?.fat ?? '');
          setSteps(d?.steps ?? '');
          setSleepHours(d?.sleepHours ?? '');
          // Prefill targets tab using current top-level settings when opening
          setTCalorieTarget(Number(localStorage.getItem('health.calorieTarget') || calorieTarget || 0));
          setTWeightTarget(Number(localStorage.getItem('health.weightTarget') || weightTarget || 0));
          setTBfMin(Number(localStorage.getItem('health.bfTargetMin') || bfTargetMin || 0));
          setTBfMax(Number(localStorage.getItem('health.bfTargetMax') || bfTargetMax || 0));
        }
      } catch (e) {
        if (!cancelled) setEntryError(e.message || 'Error cargando dato diario');
      } finally {
        if (!cancelled) setEntryLoading(false);
      }
    }
    loadExisting();
    return () => { cancelled = true; };
  }, [isEntryOpen, dateId, currentUser, currentEntity, calorieTarget, weightTarget, bfTargetMin, bfTargetMax]);

  const resetEntryForm = () => {
    setDateId(businessDateChile06());
    setWeightKg(''); setBodyFatPct(''); setCalories(''); setNotes('');
    setChest(''); setWaist(''); setHip('');
    setProtein(''); setCarbs(''); setFat('');
    setSteps(''); setSleepHours('');
    setEntryError(''); setEntrySaved(false);
    setEntryTab('health');
    setTCalorieTarget(Number(localStorage.getItem('health.calorieTarget') || calorieTarget || 0));
    setTWeightTarget(Number(localStorage.getItem('health.weightTarget') || weightTarget || 0));
    setTBfMin(Number(localStorage.getItem('health.bfTargetMin') || bfTargetMin || 0));
    setTBfMax(Number(localStorage.getItem('health.bfTargetMax') || bfTargetMax || 0));
  };

  return (
    <div className="health-dashboard-page">
      <Header currentPage="health" pageTitle="Salud & Fitness" titleSuffix={(
        <>
          <div className="asof" style={{ padding: '2px 8px', borderRadius: 6, background: 'var(--panel-bg, rgba(0,0,0,0.06))', fontSize: 13 }}>
            Corte: 06:00 America/Santiago
          </div>
          <button className="btn-secondary" style={{ marginLeft: 8 }} title="Ajustes" onClick={() => setSettingsOpen(true)}>⚙️</button>
          <button className="btn-primary" style={{ marginLeft: 8 }} title="Registrar salud" onClick={() => { resetEntryForm(); setEntryOpen(true); }}>📝 Registrar</button>
        </>
      )} />

      {/* Warning row and KPI bar directly under header */}
      {leanRisk && (
        <div style={{
          padding: '6px 10px',
          borderRadius: 6,
          background: isDark ? 'rgba(251,191,36,.20)' : 'rgba(245,158,11,.15)',
          color: isDark ? '#fde68a' : '#92400e',
          margin: '8px 0'
        }}>
          ⚠️ Alta proporción de pérdida magra en 14d. Revisa proteína, volumen e intensidad del entrenamiento, y/o ritmo de déficit.
        </div>
      )}
      <div className="kpi-bar" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 8, padding: '6px 0', marginBottom: 8 }}>
        <div className="kpi-chip" title={`Spot | ${trendLabel}`} style={{ background: 'var(--panel-bg, rgba(0,0,0,0.05))', borderRadius: 8, padding: '8px 10px' }}>
          <div style={{ fontSize: 12, opacity: .8 }}>Peso <span className="info" title="spot | tendencia">ℹ️</span></div>
          <div style={{ fontWeight: 600 }}>{latestWeight.toFixed(1)} kg | {latestWeightTrend.toFixed(1)} kg</div>
        </div>
        <div className="kpi-chip" title={`Spot | ${trendLabel}`} style={{ background: 'var(--panel-bg, rgba(0,0,0,0.05))', borderRadius: 8, padding: '8px 10px' }}>
          <div style={{ fontSize: 12, opacity: .8 }}>% Grasa <span className="info" title="spot | tendencia">ℹ️</span></div>
          <div style={{ fontWeight: 600 }}>{latestBf.toFixed(1)}% | {latestBfTrend.toFixed(1)}%</div>
        </div>
        <div className="kpi-chip" title={`Spot | ${trendLabel}`} style={{ background: 'var(--panel-bg, rgba(0,0,0,0.05))', borderRadius: 8, padding: '8px 10px' }}>
          <div style={{ fontSize: 12, opacity: .8 }}>Masa Grasa <span className="info" title="spot | tendencia derivada">ℹ️</span></div>
          <div style={{ fontWeight: 600 }}>{latestSpotFat.toFixed(1)} kg | {latestFat.toFixed(1)} kg</div>
        </div>
        <div className="kpi-chip" title={`Spot | ${trendLabel}`} style={{ background: 'var(--panel-bg, rgba(0,0,0,0.05))', borderRadius: 8, padding: '8px 10px' }}>
          <div style={{ fontSize: 12, opacity: .8 }}>Masa Magra <span className="info" title="spot | tendencia derivada">ℹ️</span></div>
          <div style={{ fontWeight: 600 }}>{latestSpotLean.toFixed(1)} kg | {latestLean.toFixed(1)} kg</div>
        </div>
        <div className="kpi-chip" style={{ background: 'var(--panel-bg, rgba(0,0,0,0.05))', borderRadius: 8, padding: '8px 10px' }}>
          <div style={{ fontSize: 12, opacity: .8 }}>Déficit 7d <span className="info" title="Promedio diario estimado 7d basado en cambios de grasa y magra.">ℹ️</span></div>
          <div style={{ fontWeight: 600 }}>{Math.round(netDaily7)} kcal/d</div>
        </div>
        <div className="kpi-chip" style={{ background: 'var(--panel-bg, rgba(0,0,0,0.05))', borderRadius: 8, padding: '8px 10px' }}>
          <div style={{ fontSize: 12, opacity: .8 }}>Proporción 14d <span className="info" title="Distribución de Δ peso 14d entre grasa y magra.">ℹ️</span></div>
          <div style={{ fontWeight: 600 }}>{shares14.fatPct.toFixed(0)}% grasa / {shares14.leanPct.toFixed(0)}% magra</div>
        </div>
        
        <div className="kpi-chip" style={{ background: 'var(--panel-bg, rgba(0,0,0,0.05))', borderRadius: 8, padding: '8px 10px' }}>
          <div style={{ fontSize: 12, opacity: .8 }}>Ritmo 14d <span className="info" title="Ritmo semanal derivado de 14d.">ℹ️</span></div>
          <div style={{ fontWeight: 600 }}>{rate14.toFixed(2)} kg/sem ({rateClass})</div>
        </div>
      </div>

      {isSettingsOpen && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setSettingsOpen(false); }}>
          <div className="modal-content" role="dialog" aria-modal="true" aria-labelledby="health-settings-title">
            <div className="modal-header">
              <div id="health-settings-title" style={{ fontWeight: 600 }}>Ajustes de Salud</div>
              <button className="btn-secondary" onClick={() => setSettingsOpen(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="modal-section">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <label htmlFor="year" style={{ fontSize: 13, color: 'var(--muted-fg,#555)' }}>Año</label>
                    <select id="year" value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
                      {(availableYears.length ? availableYears : [new Date().getFullYear()]).map(y => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <label htmlFor="period" style={{ fontSize: 13, color: 'var(--muted-fg,#555)' }}>Período</label>
                    <select id="period" value={period} onChange={(e) => setPeriod(e.target.value)}>
                      <option value="YEAR">Año completo</option>
                      <option value="D30">Últimos 30 días</option>
                      <option value="D60">Últimos 60 días</option>
                      <option value="D90">Últimos 90 días</option>
                      <option value="CUSTOM">Personalizado</option>
                    </select>
                  </div>
                  {period === 'CUSTOM' && (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <label style={{ fontSize: 13, color: 'var(--muted-fg,#555)' }}>Desde</label>
                      <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
                      <label style={{ fontSize: 13, color: 'var(--muted-fg,#555)' }}>Hasta</label>
                      <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-section">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <label htmlFor="trend" style={{ fontSize: 13, color: 'var(--muted-fg,#555)' }}>Tendencia</label>
                    <select id="trend" value={trend} onChange={async (e) => { const v = e.target.value; setTrend(v); try { if (currentUser && currentEntity) await upsertSettings(currentUser.uid, currentEntity.id, { trend: v }); } catch {} }}>
                      <option value="SMA7">Promedio móvil 7d</option>
                      <option value="SMA14">Promedio móvil 14d</option>
                      <option value="EWMA">EWMA (α=0.3)</option>
                      <option value="MED7">Mediana móvil 7d</option>
                      <option value="LOWESS">LOWESS</option>
                    </select>
                  </div>
                  <div className="config-inline" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <label style={{ fontSize: 13, color: 'var(--muted-fg,#555)' }}>α</label>
                    <input type="number" min="0.05" max="0.9" step="0.05" value={alpha}
                      onChange={async (e) => { const v = Number(e.target.value); setAlpha(v); localStorage.setItem('health.alpha', String(v)); try { if (currentUser && currentEntity) await upsertSettings(currentUser.uid, currentEntity.id, { alpha: v }); } catch {} }} style={{ width: 70 }} />
                    <label style={{ fontSize: 13, color: 'var(--muted-fg,#555)' }}>kcal/kg grasa</label>
                    <input type="number" min="1000" max="10000" step="100" value={kcalFat}
                      onChange={async (e) => { const v = Number(e.target.value); setKcalFat(v); localStorage.setItem('health.kcalFat', String(v)); try { if (currentUser && currentEntity) await upsertSettings(currentUser.uid, currentEntity.id, { kcalFat: v }); } catch {} }} style={{ width: 90 }} />
                    <label style={{ fontSize: 13, color: 'var(--muted-fg,#555)' }}>kcal/kg magra</label>
                    <input type="number" min="100" max="5000" step="50" value={kcalLean}
                      onChange={async (e) => { const v = Number(e.target.value); setKcalLean(v); localStorage.setItem('health.kcalLean', String(v)); try { if (currentUser && currentEntity) await upsertSettings(currentUser.uid, currentEntity.id, { kcalLean: v }); } catch {} }} style={{ width: 90 }} />
                    <label style={{ fontSize: 13, color: 'var(--muted-fg,#555)' }}>LOWESS span</label>
                    <input type="number" min="0.1" max="0.9" step="0.05" value={lowessSpan}
                      onChange={async (e) => { const v = Number(e.target.value); setLowessSpan(v); localStorage.setItem('health.lowessSpan', String(v)); try { if (currentUser && currentEntity) await upsertSettings(currentUser.uid, currentEntity.id, { lowessSpan: v }); } catch {} }} style={{ width: 80 }} />
                    <label style={{ fontSize: 13, color: 'var(--muted-fg,#555)' }}>Bandas</label>
                    <input type="checkbox" checked={showBands} onChange={async (e) => { const v = e.target.checked; setShowBands(v); localStorage.setItem('health.showBands', v ? '1' : '0'); try { if (currentUser && currentEntity) await upsertSettings(currentUser.uid, currentEntity.id, { showBands: v }); } catch {} }} />
                    <label style={{ fontSize: 13, color: 'var(--muted-fg,#555)' }}>Método</label>
                    <select value={bandMethod} onChange={async (e) => { const v = e.target.value; setBandMethod(v); localStorage.setItem('health.bandMethod', v); try { if (currentUser && currentEntity) await upsertSettings(currentUser.uid, currentEntity.id, { bandMethod: v }); } catch {} }}>
                      <option value="MAD">MAD</option>
                      <option value="STD">STD</option>
                    </select>
                    <label style={{ fontSize: 13, color: 'var(--muted-fg,#555)' }}>Ventana</label>
                    <input type="number" min="7" max="60" step="1" value={bandWindow} onChange={async (e) => { const v = Number(e.target.value); setBandWindow(v); localStorage.setItem('health.bandWindow', String(v)); try { if (currentUser && currentEntity) await upsertSettings(currentUser.uid, currentEntity.id, { bandWindow: v }); } catch {} }} style={{ width: 70 }} />
                    <label style={{ fontSize: 13, color: 'var(--muted-fg,#555)' }}>k</label>
                    <input type="number" min="0.5" max="3" step="0.1" value={bandScale} onChange={async (e) => { const v = Number(e.target.value); setBandScale(v); localStorage.setItem('health.bandScale', String(v)); try { if (currentUser && currentEntity) await upsertSettings(currentUser.uid, currentEntity.id, { bandScale: v }); } catch {} }} style={{ width: 70 }} />
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
                  <div className="config-inline" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <label style={{ fontSize: 13, color: 'var(--muted-fg,#555)' }}>Objetivo</label>
                    <select value={goal} onChange={(e) => { const v = e.target.value; setGoal(v); localStorage.setItem('health.goal', v); }}>
                      <option value="cutting">Cutting</option>
                      <option value="bulking">Bulking</option>
                    </select>
                    <label style={{ fontSize: 13, color: 'var(--muted-fg,#555)' }}>Meta kcal/d</label>
                    <input type="number" min="1000" max="6000" step="50" value={calorieTarget}
                      onChange={(e) => { const v = Number(e.target.value); setCalorieTarget(v); localStorage.setItem('health.calorieTarget', String(v)); }} style={{ width: 90 }} />
                    <button className="btn-secondary" onClick={async () => {
                      try {
                        if (!currentUser || !currentEntity) return;
                        const startId = (viewDailies && viewDailies.length) ? viewDailies[0].id : `${selectedYear}-01-01`;
                        await upsertGoal(currentUser.uid, currentEntity.id, { type: 'calorieTarget', startId, value: calorieTarget });
                        const gs = await getGoals(currentUser.uid, currentEntity.id, 'calorieTarget');
                        setGoals(gs || []);
                        const ts = await getTargets(currentUser.uid, currentEntity.id, 'calorieTarget');
                        setCalTargets(ts || []);
                      } catch (e) {
                        setError(e?.message || 'Error guardando meta');
                      }
                    }}>Guardar meta desde período</button>
                    <button className="btn-secondary" onClick={async () => {
                      try {
                        if (!currentUser || !currentEntity) return;
                        const startId = (viewDailies && viewDailies.length) ? viewDailies[0].id : `${selectedYear}-01-01`;
                        await upsertTarget(currentUser.uid, currentEntity.id, { type: 'calorieTarget', startId, value: calorieTarget });
                      } catch (e) { setError(e?.message || 'Error guardando target'); }
                    }}>Versionar meta (targets)</button>
                    <label style={{ fontSize: 13, color: 'var(--muted-fg,#555)' }}>Vista calorías</label>
                    <select value={calorieView} onChange={async (e) => { const v = e.target.value; setCalorieView(v); localStorage.setItem('health.calorieView', v); try { if (currentUser && currentEntity) await upsertSettings(currentUser.uid, currentEntity.id, { calorieView: v }); } catch {} }}>
                      <option value="daily">Diaria</option>
                      <option value="weekly">Semanal</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                  <button className="btn-secondary" onClick={() => {
                    try {
                      const defById = new Map((dailyDeficit || []).map(s => [s.id, Number(s.value || 0)]));
                      const rows = (viewDailies || []).map(d => {
                        const w = Number(d.weightKg || 0);
                        const bf = Number(d.bodyFatPct || 0) / 100;
                        const fat = w * bf;
                        const lean = Math.max(0, w - fat);
                        return {
                          id: d.id,
                          weightKg: w,
                          bodyFatPct: Number(d.bodyFatPct || 0),
                          calories: Number(d.calories || 0),
                          fatMassKg: fat,
                          leanMassKg: lean,
                          dailyDeficitKcal: defById.get(d.id) ?? '',
                          chest: d?.circumferences?.chest ?? '',
                          waist: d?.circumferences?.waist ?? '',
                          hip: d?.circumferences?.hip ?? '',
                          protein: d?.macros?.protein ?? '',
                          carbs: d?.macros?.carbs ?? '',
                          fatMacro: d?.macros?.fat ?? '',
                          steps: d?.steps ?? '',
                          sleepHours: d?.sleepHours ?? '',
                        };
                      });
                      const header = ['dateId','weightKg','bodyFatPct','calories','fatMassKg','leanMassKg','dailyDeficitKcal','chest','waist','hip','protein','carbs','fat','steps','sleepHours'];
                      const csv = [header.join(','), ...rows.map(r => [r.id, r.weightKg, r.bodyFatPct, r.calories, r.fatMassKg, r.leanMassKg, r.dailyDeficitKcal, r.chest, r.waist, r.hip, r.protein, r.carbs, r.fatMacro, r.steps, r.sleepHours].join(','))].join('\n');
                      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `health_${period.toLowerCase()}_${selectedYear}.csv`;
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch {}
                  }}>Exportar CSV</button>
                  <label className="btn-secondary" style={{ cursor: 'pointer' }}>
                    Importar CSV
                    <input type="file" accept=".csv" style={{ display: 'none' }} onChange={async (e) => {
                      const file = e.target.files && e.target.files[0];
                      if (!file) return;
                      try {
                        const text = await file.text();
                        const lines = text.split(/\r?\n/).filter(Boolean);
                        const header = lines[0].split(',').map(s => s.trim());
                        const idx = (name) => header.findIndex(h => h.toLowerCase() === name.toLowerCase());
                        const iDate = idx('dateId'); const iW = idx('weightKg'); const iBf = idx('bodyFatPct'); const iCal = idx('calories'); const iNotes = idx('notes');
                        const iChest = idx('chest'); const iWaist = idx('waist'); const iHip = idx('hip');
                        const iProt = idx('protein'); const iCarb = idx('carbs'); const iFat = idx('fat');
                        const iSteps = idx('steps'); const iSleep = idx('sleepHours');
                        const entries = lines.slice(1).map(line => {
                          const parts = line.split(',');
                          return {
                            dateId: parts[iDate],
                            weightKg: parts[iW],
                            bodyFatPct: parts[iBf],
                            calories: parts[iCal],
                            notes: iNotes >= 0 ? parts[iNotes] : undefined,
                            chest: iChest >= 0 ? parts[iChest] : undefined,
                            waist: iWaist >= 0 ? parts[iWaist] : undefined,
                            hip: iHip >= 0 ? parts[iHip] : undefined,
                            protein: iProt >= 0 ? parts[iProt] : undefined,
                            carbs: iCarb >= 0 ? parts[iCarb] : undefined,
                            fat: iFat >= 0 ? parts[iFat] : undefined,
                            steps: iSteps >= 0 ? parts[iSteps] : undefined,
                            sleepHours: iSleep >= 0 ? parts[iSleep] : undefined,
                          };
                        });
                        setLoading(true);
                        await bulkUpsertDaily(currentUser.uid, currentEntity.id, entries);
                        const { years } = await getYearRange(currentUser.uid, currentEntity.id);
                        setAvailableYears(years || []);
                        const list = period === 'YEAR' ? await getDailyForYear(currentUser.uid, currentEntity.id, selectedYear) : (await getDailyRange(currentUser.uid, currentEntity.id, viewDailies[0]?.id || `${selectedYear}-01-01`, viewDailies[viewDailies.length-1]?.id || `${selectedYear}-12-31`));
                        setDailies(list || []);
                        setLoading(false);
                      } catch (err) {
                        setError(err?.message || 'Error importando CSV');
                        setLoading(false);
                      } finally {
                        e.target.value = '';
                      }
                    }} />
                  </label>
                  <button className="btn-secondary" onClick={async () => {
                    if (!viewDailies || viewDailies.length === 0) return;
                    const last = viewDailies[viewDailies.length - 1]?.id;
                    if (!last) return;
                    if (!window.confirm(`Eliminar el registro del ${last}?`)) return;
                    try {
                      setLoading(true);
                      await deleteDaily(currentUser.uid, currentEntity.id, last);
                      const list = period === 'YEAR' ? await getDailyForYear(currentUser.uid, currentEntity.id, selectedYear) : (await getDailyRange(currentUser.uid, currentEntity.id, viewDailies[0]?.id || `${selectedYear}-01-01`, last));
                      setDailies(list || []);
                    } catch (e) {
                      setError(e?.message || 'Error eliminando registro');
                    } finally {
                      setLoading(false);
                    }
                  }}>Eliminar último</button>
                </div>
              </div>

              <div className="modal-section">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
                  <div className="config-inline" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <label style={{ fontSize: 13, color: 'var(--muted-fg,#555)' }}>Objetivo peso (kg)</label>
                    <input type="number" step="0.1" value={weightTarget} onChange={(e) => { const v = Number(e.target.value); setWeightTarget(v); localStorage.setItem('health.weightTarget', String(v)); }} style={{ width: 90 }} />
                    <button className="btn-secondary" onClick={async () => {
                      try {
                        if (!currentUser || !currentEntity) return;
                        const startId = (viewDailies && viewDailies.length) ? viewDailies[0].id : `${selectedYear}-01-01`;
                        await upsertTarget(currentUser.uid, currentEntity.id, { type: 'weightTarget', startId, value: weightTarget });
                      } catch (e) { setError(e?.message || 'Error guardando objetivo de peso'); }
                    }}>Versionar objetivo peso</button>
                    <label style={{ fontSize: 13, color: 'var(--muted-fg,#555)' }}>%Grasa objetivo</label>
                    <input type="number" step="0.1" placeholder="min" value={bfTargetMin} onChange={(e) => { const v = Number(e.target.value); setBfTargetMin(v); localStorage.setItem('health.bfTargetMin', String(v)); }} style={{ width: 80 }} />
                    <input type="number" step="0.1" placeholder="max" value={bfTargetMax} onChange={(e) => { const v = Number(e.target.value); setBfTargetMax(v); localStorage.setItem('health.bfTargetMax', String(v)); }} style={{ width: 80 }} />
                    <button className="btn-secondary" onClick={async () => {
                      try {
                        if (!currentUser || !currentEntity) return;
                        const startId = (viewDailies && viewDailies.length) ? viewDailies[0].id : `${selectedYear}-01-01`;
                        await upsertTarget(currentUser.uid, currentEntity.id, { type: 'bfRange', startId, min: bfTargetMin, max: bfTargetMax });
                      } catch (e) { setError(e?.message || 'Error guardando rango %grasa'); }
                    }}>Versionar %grasa objetivo</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isEntryOpen && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setEntryOpen(false); }}>
          <div className="modal-content" role="dialog" aria-modal="true" aria-labelledby="health-entry-title">
            <div className="modal-header">
              <div id="health-entry-title" style={{ fontWeight: 600 }}>Registro de salud</div>
              <button className="btn-secondary" onClick={() => setEntryOpen(false)}>✕</button>
            </div>
            <div className="modal-body">
              {/* Tabs */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                <button className={entryTab === 'health' ? 'btn-primary' : 'btn-secondary'} type="button" onClick={() => setEntryTab('health')}>Salud</button>
                <button className={entryTab === 'targets' ? 'btn-primary' : 'btn-secondary'} type="button" onClick={() => setEntryTab('targets')}>Targets</button>
              </div>

              {entryTab === 'health' && (
              <form className="form" onSubmit={async (e) => {
                e.preventDefault();
                try {
                  setEntryLoading(true); setEntryError(''); setEntrySaved(false);
                  // Build nested objects without undefineds (Firestore no admite undefined)
                  const circumferences = {};
                  if (chest !== '' && chest != null) circumferences.chest = Number(chest);
                  if (waist !== '' && waist != null) circumferences.waist = Number(waist);
                  if (hip !== '' && hip != null) circumferences.hip = Number(hip);
                  const macrosObj = {};
                  if (protein !== '' && protein != null) macrosObj.protein = Number(protein);
                  if (carbs !== '' && carbs != null) macrosObj.carbs = Number(carbs);
                  if (fat !== '' && fat != null) macrosObj.fat = Number(fat);
                  const entry = {
                    dateId,
                    // requeridos por el form
                    weightKg: Number(weightKg),
                    bodyFatPct: Number(bodyFatPct),
                    // opcionales
                    ...(calories === '' ? {} : { calories: Number(calories) }),
                    ...(notes ? { notes: String(notes) } : {}),
                    ...(Object.keys(circumferences).length ? { circumferences } : {}),
                    ...(Object.keys(macrosObj).length ? { macros: macrosObj } : {}),
                    ...(steps === '' ? {} : { steps: Number(steps) }),
                    ...(sleepHours === '' ? {} : { sleepHours: Number(sleepHours) }),
                  };
                  await upsertDaily(currentUser.uid, currentEntity.id, entry);
                  setEntrySaved(true);
                  // Reload dashboard data after save
                  const { years } = await getYearRange(currentUser.uid, currentEntity.id);
                  setAvailableYears(years || []);
                  const list = period === 'YEAR'
                    ? await getDailyForYear(currentUser.uid, currentEntity.id, selectedYear)
                    : await getDailyRange(currentUser.uid, currentEntity.id, viewDailies[0]?.id || `${selectedYear}-01-01`, viewDailies[viewDailies.length-1]?.id || `${selectedYear}-12-31`);
                  setDailies(list || []);
                } catch (e) {
                  setEntryError(e.message || 'Error guardando');
                } finally {
                  setEntryLoading(false);
                }
              }}>
                <div className="form-grid">
                  <div className="form-row">
                    <label>Fecha</label>
                    <input type="date" value={dateId} onChange={(e) => setDateId(e.target.value)} />
                    <div className="hint">Corte: {businessDateChile06()}</div>
                  </div>
                  <div className="form-row">
                    <label>Peso (kg)</label>
                    <input type="number" step="0.1" min="20" max="250" value={weightKg} onChange={(e) => setWeightKg(e.target.value)} required />
                  </div>
                  <div className="form-row">
                    <label>% Grasa</label>
                    <input type="number" step="0.1" min="3" max="60" value={bodyFatPct} onChange={(e) => setBodyFatPct(e.target.value)} required />
                  </div>
                  <div className="form-row">
                    <label>Calorías</label>
                    <input type="number" step="1" min="0" max="10000" value={calories} onChange={(e) => setCalories(e.target.value)} />
                  </div>
                  <div className="form-row">
                    <label>Notas</label>
                    <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Opcional: sensaciones, entrenamiento, sueño, agua, etc." />
                  </div>
                  <div className="form-row">
                    <label>Circunferencias (cm)</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                      <input type="number" step="0.1" placeholder="Pecho" value={chest} onChange={(e) => setChest(e.target.value)} />
                      <input type="number" step="0.1" placeholder="Cintura" value={waist} onChange={(e) => setWaist(e.target.value)} />
                      <input type="number" step="0.1" placeholder="Cadera" value={hip} onChange={(e) => setHip(e.target.value)} />
                    </div>
                  </div>
                  <div className="form-row">
                    <label>Macros (g)</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                      <input type="number" step="1" placeholder="Proteína" value={protein} onChange={(e) => setProtein(e.target.value)} />
                      <input type="number" step="1" placeholder="Carbos" value={carbs} onChange={(e) => setCarbs(e.target.value)} />
                      <input type="number" step="1" placeholder="Grasa" value={fat} onChange={(e) => setFat(e.target.value)} />
                    </div>
                  </div>
                  <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                    <div>
                      <label>Pasos</label>
                      <input type="number" step="1" min="0" value={steps} onChange={(e) => setSteps(e.target.value)} />
                    </div>
                    <div>
                      <label>Horas de Sueño</label>
                      <input type="number" step="0.1" min="0" max="24" value={sleepHours} onChange={(e) => setSleepHours(e.target.value)} />
                    </div>
                  </div>
                </div>
                {entryError && <div className="state error" style={{ marginTop: 8 }}>{entryError}</div>}
                {entrySaved && <div className="state" style={{ marginTop: 8 }}>Guardado ✅</div>}
                <div className="actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
                  <button type="button" className="btn-secondary" onClick={() => setEntryOpen(false)}>Cancelar</button>
                  <button type="submit" className="btn-primary" disabled={entryLoading}>{entryLoading ? 'Guardando…' : 'Guardar'}</button>
                </div>
              </form>
              )}

              {entryTab === 'targets' && (
                <div>
                  <div className="form-grid">
                    <div className="form-row">
                      <label>Fecha de inicio (aplicación)</label>
                      <input type="date" value={dateId} onChange={(e) => setDateId(e.target.value)} />
                      <div className="hint">Los targets quedan versionados desde esta fecha</div>
                    </div>
                    <div className="form-row">
                      <label>Meta Calorías (kcal/día)</label>
                      <input type="number" step="50" min="500" max="8000" value={tCalorieTarget} onChange={(e) => setTCalorieTarget(Number(e.target.value))} />
                    </div>
                    <div className="form-row">
                      <label>Objetivo Peso (kg)</label>
                      <input type="number" step="0.1" min="20" max="250" value={tWeightTarget} onChange={(e) => setTWeightTarget(Number(e.target.value))} />
                    </div>
                    <div className="form-row">
                      <label>Rango % Grasa objetivo</label>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                        <input type="number" step="0.1" min="3" max="60" placeholder="min" value={tBfMin} onChange={(e) => setTBfMin(Number(e.target.value))} />
                        <input type="number" step="0.1" min="3" max="60" placeholder="max" value={tBfMax} onChange={(e) => setTBfMax(Number(e.target.value))} />
                      </div>
                    </div>
                  </div>
                  <div className="actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
                    <button type="button" className="btn-secondary" onClick={() => setEntryOpen(false)}>Cancelar</button>
                    <button type="button" className="btn-primary" disabled={entryLoading} onClick={async () => {
                      try {
                        setEntryLoading(true); setEntryError(''); setEntrySaved(false);
                        if (!currentUser || !currentEntity) return;
                        const startId = dateId;
                        if (Number.isFinite(Number(tCalorieTarget)) && Number(tCalorieTarget) > 0) {
                          await upsertTarget(currentUser.uid, currentEntity.id, { type: 'calorieTarget', startId, value: Number(tCalorieTarget) });
                          setCalorieTarget(Number(tCalorieTarget)); localStorage.setItem('health.calorieTarget', String(Number(tCalorieTarget)));
                          const ts = await getTargets(currentUser.uid, currentEntity.id, 'calorieTarget');
                          setCalTargets(ts || []);
                        }
                        if (Number.isFinite(Number(tWeightTarget)) && Number(tWeightTarget) > 0) {
                          await upsertTarget(currentUser.uid, currentEntity.id, { type: 'weightTarget', startId, value: Number(tWeightTarget) });
                          setWeightTarget(Number(tWeightTarget)); localStorage.setItem('health.weightTarget', String(Number(tWeightTarget)));
                        }
                        if (Number.isFinite(Number(tBfMin)) && Number.isFinite(Number(tBfMax)) && Number(tBfMin) > 0 && Number(tBfMax) > 0) {
                          await upsertTarget(currentUser.uid, currentEntity.id, { type: 'bfRange', startId, min: Number(tBfMin), max: Number(tBfMax) });
                          setBfTargetMin(Number(tBfMin)); setBfTargetMax(Number(tBfMax)); localStorage.setItem('health.bfTargetMin', String(Number(tBfMin))); localStorage.setItem('health.bfTargetMax', String(Number(tBfMax)));
                        }
                        setEntrySaved(true);
                      } catch (e) {
                        setEntryError(e?.message || 'Error guardando targets');
                      } finally {
                        setEntryLoading(false);
                      }
                    }}>{entryLoading ? 'Guardando…' : 'Guardar targets'}</button>
                  </div>
                  {entryError && <div className="state error" style={{ marginTop: 8 }}>{entryError}</div>}
                  {entrySaved && <div className="state" style={{ marginTop: 8 }}>Targets guardados ✅</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {loading && <div className="state">Cargando…</div>}
      {error && <div className="state error">{error}</div>}

      {!loading && (
        <div className="grid-3">
          <div className="panel">
            <div className="panel-header">Peso (kg)</div>
            <HealthOverlayLineChart
              seriesList={[
                { series: toSeries(viewDailies, 'weightKg'), color: '#10b981', name: 'Peso', render: 'points', pointRadius: 2.5, pointOpacity: 0.6 },
                { series: weightTrend, color: (isDark ? '#f59e0b' : '#111827'), name: 'Tendencia' }
              ]}
              bands={[
                showBands ? { upper: weightBand.upper, lower: weightBand.lower, color: '#10b981', opacity: 0.2 } : null,
                (weightTarget > 0 ? { upper: weightTrend.map(p => ({ id: p.id, value: weightTarget })), lower: weightTrend.map(p => ({ id: p.id, value: weightTarget })), color: '#3b82f6', opacity: 0.15 } : null)
              ].filter(Boolean)}
              height={200}
              valueSuffix="kg"
              hoverId={sharedHoverIndex}
              onHoverIdChange={setSharedHoverIndex}
              interactiveLegend
            />
          </div>
          <div className="panel">
            <div className="panel-header">% Grasa</div>
            <HealthOverlayLineChart
              seriesList={[
                { series: toSeries(viewDailies, 'bodyFatPct'), color: '#ef4444', name: 'Grasa', render: 'points', pointRadius: 2.5, pointOpacity: 0.6 },
                { series: bfTrend, color: (isDark ? '#f59e0b' : '#111827'), name: 'Tendencia' }
              ]}
              bands={[
                showBands ? { upper: bfBand.upper, lower: bfBand.lower, color: '#ef4444', opacity: 0.2 } : null,
                (bfTargetMin > 0 && bfTargetMax > 0 ? {
                  upper: bfTrend.map(p => ({ id: p.id, value: Math.max(bfTargetMin, bfTargetMax) })),
                  lower: bfTrend.map(p => ({ id: p.id, value: Math.min(bfTargetMin, bfTargetMax) })),
                  color: '#3b82f6', opacity: 0.1
                } : null)
              ].filter(Boolean)}
              height={200}
              valueSuffix="%"
              hoverId={sharedHoverIndex}
              onHoverIdChange={setSharedHoverIndex}
              interactiveLegend
            />
          </div>
          <div className="panel">
            <div className="panel-header">Calorías</div>
            <HealthOverlayLineChart
              seriesList={[
                { series: (calorieView === 'weekly' ? calWeekly : toSeries(viewDailies, 'calories')), color: '#3b82f6', name: (calorieView === 'weekly' ? 'Calorías (sem.)' : 'Calorías'), render: 'points', pointRadius: 2.5, pointOpacity: 0.6 },
                { series: calTrend, color: (isDark ? '#f59e0b' : '#111827'), name: 'Tendencia' },
                { series: calTargetSeriesDaily, color: '#f97316', name: 'Meta' }
              ]}
              bands={[
                // Volatility band only for daily view
                (calorieView === 'daily' && showBands ? { upper: calBand.upper, lower: calBand.lower, color: '#3b82f6', opacity: 0.18 } : null),
                // Historical target band derived from versioned goals
                (() => {
                  const src = (calorieView === 'weekly' ? calWeekly : toSeries(viewDailies, 'calories'));
                  const targets = buildTargetSeries(src, (calTargets && calTargets.length ? calTargets : goals), calorieTarget);
                  return {
                    upper: targets.map(p => ({ id: p.id, value: Number(p.value) * 1.05 })),
                    lower: targets.map(p => ({ id: p.id, value: Math.max(0, Number(p.value) * 0.95) })),
                    color: '#f97316', opacity: 0.18
                  };
                })()
              ].filter(Boolean)}
              height={200}
              valueSuffix="kcal"
              hoverId={sharedHoverIndex}
              onHoverIdChange={setSharedHoverIndex}
              interactiveLegend
            />
          </div>
        </div>
      )}

      {!loading && (
        <div className="grid-2" style={{ marginTop: 12 }}>
          <div className="panel">
            <div className="panel-header">Déficit / Superávit estimado (diario)</div>
            <HealthOverlayLineChart
              seriesList={[
                { series: dailyEnergyBalance, color: '#ef4444', name: 'Balance energético', render: 'points', pointRadius: 0, pointOpacity: 0 },
                { series: calIntakeVsTarget7, color: (isDark ? '#f59e0b' : '#111827'), name: 'Ingesta - Meta (7d)' },
              ]}
              bands={[
                // Positive = superávit (green)
                { upper: energyPosBand.upper, lower: energyPosBand.lower, color: '#10b981', opacity: 0.22 },
                // Negative = déficit (red)
                { upper: energyNegBand.upper, lower: energyNegBand.lower, color: '#ef4444', opacity: 0.22 },
              ]}
              height={220}
              valueSuffix="kcal"
              yIncludeZero
              yTickStep={200}
              renderTooltip={({ id, nearestIndexById }) => {
                // Build tooltip rows with underlying components used for calculation
                // Reference vs 7 días atrás
                const toMinus7 = (iso) => {
                  const [y, m, d] = String(iso).split('-').map(Number);
                  const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
                  dt.setUTCDate(dt.getUTCDate() - 7);
                  const yy = dt.getUTCFullYear();
                  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
                  const dd = String(dt.getUTCDate()).padStart(2, '0');
                  return `${yy}-${mm}-${dd}`;
                };
                const refId = toMinus7(id);
                // Current indices
                const iFat = nearestIndexById(fatTrend || [], id);
                const iLean = nearestIndexById(leanTrend || [], id);
                const curFat = (fatTrend[iFat] || {}).value;
                const curLean = (leanTrend[iLean] || {}).value;
                // Indices exactly 7d atrás (exigen match exacto)
                const jFatCand = nearestIndexById(fatTrend || [], refId);
                const jLeanCand = nearestIndexById(leanTrend || [], refId);
                const jFat = (fatTrend[jFatCand]?.id === refId) ? jFatCand : null;
                const jLean = (leanTrend[jLeanCand]?.id === refId) ? jLeanCand : null;
                const prevFat = jFat != null ? (fatTrend[jFat] || {}).value : undefined;
                const prevLean = jLean != null ? (leanTrend[jLean] || {}).value : undefined;
                const dFat = Number(curFat || 0) - Number(prevFat ?? curFat ?? 0);
                const dLean = Number(curLean || 0) - Number(prevLean ?? curLean ?? 0);
                const energyChange = dFat * (kcalFat || 0) + dLean * (kcalLean || 0);
                const net = energyChange; // semanal
                const netDaily = net / 7; // promedio diario
                const dateTxt = formatDateShort(id);
                const refTxt = formatDateShort(refId);
                const curW = Number(curFat || 0) + Number(curLean || 0);
                const prevW = Number(prevFat ?? curFat ?? 0) + Number(prevLean ?? curLean ?? 0);
                const dW = curW - prevW;
                // Also compute intake-target 7d diff for this id
                const idxCT = nearestIndexById(calIntakeVsTarget7 || [], id);
                const ptCT = (calIntakeVsTarget7 || [])[idxCT];
                const ctVal = Number(ptCT?.value || 0);
                return (
                  <div>
                    <div style={{ marginBottom: 4, opacity: 0.9 }}>{dateTxt} vs {refTxt}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 6 }}>
                      <div>Peso:</div><div><strong>{(prevW).toFixed(2)} → {(curW).toFixed(2)} kg</strong> ({dW >= 0 ? '+' : ''}{dW.toFixed(2)} kg)</div>
                      <div>Grasa:</div><div><strong>{(Number(prevFat ?? curFat ?? 0)).toFixed(2)} → {(Number(curFat||0)).toFixed(2)} kg</strong> ({dFat >= 0 ? '+' : ''}{dFat.toFixed(2)} kg)</div>
                      <div>Magra:</div><div><strong>{(Number(prevLean ?? curLean ?? 0)).toFixed(2)} → {(Number(curLean||0)).toFixed(2)} kg</strong> ({dLean >= 0 ? '+' : ''}{dLean.toFixed(2)} kg)</div>
                      <div>Balance:</div><div><strong>{Math.round(netDaily)} kcal/d</strong> {netDaily >= 0 ? '(superávit)' : '(déficit)'}</div>
                      <div>Ingesta:</div><div><strong>{Math.round(ctVal)} kcal/d</strong></div>
                    </div>
                  </div>
                );
              }}
            />
          </div>
          <div className="panel">
            <div className="panel-header" style={{ color: isDark ? '#e5e7eb' : 'inherit' }}>
              Análisis 7d / 14d / 21d / 28d {lastDateId ? `(cierre: ${formatDateShort(lastDateId)})` : ''}
            </div>
            <div className="table-wrap">
              <table className="health-table">
                <thead>
                  <tr style={{ background: isDark ? 'rgba(255,255,255,.14)' : 'rgba(0,0,0,.06)' }}>
                    <th style={{ color: isDark ? '#ffffff' : 'inherit' }}>Métrica</th>
                    <th className="num" style={{ color: isDark ? '#ffffff' : 'inherit' }}>7 días</th>
                    <th className="num" style={{ color: isDark ? '#ffffff' : 'inherit' }}>14 días</th>
                    <th className="num" style={{ color: isDark ? '#ffffff' : 'inherit' }}>21 días</th>
                    <th className="num" style={{ color: isDark ? '#ffffff' : 'inherit' }}>28 días</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Desde</td>
                    <td className="num">{formatDateShort(toMinusDays(lastDateId, 7))}</td>
                    <td className="num">{formatDateShort(toMinusDays(lastDateId, 14))}</td>
                    <td className="num">{formatDateShort(toMinusDays(lastDateId, 21))}</td>
                    <td className="num">{formatDateShort(toMinusDays(lastDateId, 28))}</td>
                  </tr>
                  <tr>
                    <td style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>Adherencia (±5%)</span>
                    </td>
                    <td className="num">—</td>
                    <td className="num">—</td>
                    <td className="num">—</td>
                    <td className="num">—</td>
                  </tr>
                  <tr>
                    <td>Δ Peso (g)</td>
                    <td className="num">{Math.round(wDelta7 * 1000)}</td>
                    <td className="num">{Math.round(wDelta14 * 1000)}</td>
                    <td className="num">{Math.round(wDelta21 * 1000)}</td>
                    <td className="num">{Math.round(wDelta28 * 1000)}</td>
                  </tr>
                  <tr>
                    <td>Δ Grasa (g)</td>
                    <td className="num">{Math.round(fDelta7 * 1000)}</td>
                    <td className="num">{Math.round(fDelta14 * 1000)}</td>
                    <td className="num">{Math.round(fDelta21 * 1000)}</td>
                    <td className="num">{Math.round(fDelta28 * 1000)}</td>
                  </tr>
                  <tr>
                    <td>Δ Magra (g)</td>
                    <td className="num">{Math.round(lDelta7 * 1000)}</td>
                    <td className="num">{Math.round(lDelta14 * 1000)}</td>
                    <td className="num">{Math.round(lDelta21 * 1000)}</td>
                    <td className="num">{Math.round(lDelta28 * 1000)}</td>
                  </tr>
                  <tr>
                    <td>Proporción (grasa / magra)</td>
                    <td className="num">{shares7.fatPct.toFixed(0)}% / {shares7.leanPct.toFixed(0)}%</td>
                    <td className="num">{shares14.fatPct.toFixed(0)}% / {shares14.leanPct.toFixed(0)}%</td>
                    <td className="num">{shares21.fatPct.toFixed(0)}% / {shares21.leanPct.toFixed(0)}%</td>
                    <td className="num">{shares28.fatPct.toFixed(0)}% / {shares28.leanPct.toFixed(0)}%</td>
                  </tr>
                  <tr>
                    <td>Déficit calórico promedio (kcal/día)</td>
                    <td className="num">{Math.round(netDaily7)}</td>
                    <td className="num">{Math.round(netDaily14)}</td>
                    <td className="num">{Math.round(netDaily21)}</td>
                    <td className="num">{Math.round(netDaily28)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="panel">
            <div className="panel-header">Composición (Magra vs Grasa)</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <label style={{ fontSize: 13, color: 'var(--muted-fg,#555)' }}>Vista</label>
              <select value={compView} onChange={async (e) => { const v = e.target.value; setCompView(v); localStorage.setItem('health.compView', v); try { if (currentUser && currentEntity) await upsertSettings(currentUser.uid, currentEntity.id, { compView: v }); } catch {} }}>
                <option value="levels">Niveles (kg)</option>
                <option value="index">Índice Δ (g)</option>
              </select>
              {compView === 'index' && (
                <>
                  <label style={{ fontSize: 13, color: 'var(--muted-fg,#555)' }}>Base</label>
                  <select value={compBaseId} onChange={async (e) => { const v = e.target.value; setCompBaseId(v); localStorage.setItem('health.compBaseId', v); try { if (currentUser && currentEntity) await upsertSettings(currentUser.uid, currentEntity.id, { compBaseId: v }); } catch {} }}>
                    {(viewDailies || []).map(d => (
                      <option key={d.id} value={d.id}>{formatDateShort(d.id)}</option>
                    ))}
                  </select>
                  <label style={{ fontSize: 13, color: 'var(--muted-fg,#555)' }}>Diferencial</label>
                  <select value={compDiffMode} onChange={async (e) => { const v = e.target.value; setCompDiffMode(v); localStorage.setItem('health.compDiffMode', v); try { if (currentUser && currentEntity) await upsertSettings(currentUser.uid, currentEntity.id, { compDiffMode: v }); } catch {} }}>
                    <option value="trend">Tendencias</option>
                    <option value="spot">Spots</option>
                  </select>
                </>
              )}
            </div>
            {compView === 'levels' ? (
              <HealthOverlayLineChart
                seriesList={[
                  { series: fatTrend, color: '#ef4444', name: 'Grasa' },
                  { series: leanTrend, color: '#10b981', name: 'Magra' },
                ]}
                height={220}
                valueSuffix="kg"
                hoverId={sharedHoverIndex}
                onHoverIdChange={setSharedHoverIndex}
                interactiveLegend
              />
            ) : (
              <HealthOverlayLineChart
                seriesList={[
                  { series: compFatDiffG, color: '#ef4444', name: 'Δ Grasa (g)' },
                  { series: compLeanDiffG, color: '#10b981', name: 'Δ Magra (g)' },
                ]}
                bands={[
                  // Draw fat bands first, then lean bands, so stacking visual works nicely with transparency
                  { upper: compFatPosBand.upper, lower: compFatPosBand.lower, color: '#ef4444', opacity: 0.18 },
                  { upper: compFatNegBand.upper, lower: compFatNegBand.lower, color: '#ef4444', opacity: 0.18 },
                  { upper: compLeanPosBand.upper, lower: compLeanPosBand.lower, color: '#10b981', opacity: 0.18 },
                  { upper: compLeanNegBand.upper, lower: compLeanNegBand.lower, color: '#10b981', opacity: 0.18 },
                ]}
                height={240}
                valueSuffix="g"
                yIncludeZero
                yTickStep={250}
                renderTooltip={({ id, nearestIndexById, seriesList }) => {
                  const jF = nearestIndexById(compFatDiffG || [], id);
                  const jL = nearestIndexById(compLeanDiffG || [], id);
                  const pF = (compFatDiffG || [])[jF];
                  const pL = (compLeanDiffG || [])[jL];
                  const total = Number((pF?.value || 0)) + Number((pL?.value || 0));
                  const baseTxt = formatDateShort(compBaseId);
                  return (
                    <div>
                      <div style={{ marginBottom: 4, opacity: 0.9 }}>{formatDateShort(id)} vs {baseTxt}</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 6 }}>
                        <div>Δ Grasa:</div><div><strong>{Math.round(Number(pF?.value || 0))} g</strong></div>
                        <div>Δ Magra:</div><div><strong>{Math.round(Number(pL?.value || 0))} g</strong></div>
                        <div>Total:</div><div><strong>{Math.round(total)} g</strong></div>
                        <div>Modo:</div><div>{compDiffMode === 'spot' ? 'Spots' : `Tendencia (${trendLabel})`}</div>
                      </div>
                    </div>
                  );
                }}
              />
            )}
          </div>
          <div className="panel">
            <div className="panel-header">% pérdida atribuible a grasa</div>
            <HealthOverlayLineChart
              seriesList={[
                { series: compFatLossSharePct, color: '#ef4444', name: '% grasa en pérdida' },
              ]}
              height={240}
              valueSuffix="%"
              hoverId={sharedHoverIndex}
              onHoverIdChange={setSharedHoverIndex}
              yIncludeZero
              yTickStep={10}
              renderTooltip={({ id, nearestIndexById }) => {
                const j = nearestIndexById(compFatLossSharePct || [], id);
                const pt = (compFatLossSharePct || [])[j];
                const baseTxt = formatDateShort(compBaseId);
                return (
                  <div>
                    <div style={{ marginBottom: 4, opacity: 0.9 }}>{formatDateShort(id)} vs {baseTxt}</div>
                    <div><strong>{Number(pt?.value || 0).toFixed(0)}%</strong> de la pérdida es grasa</div>
                    <div style={{ opacity: .85 }}>Modo: {compDiffMode === 'spot' ? 'Spots' : `Tendencia (${trendLabel})`}</div>
                  </div>
                );
              }}
            />
          </div>
          <div className="panel">
            <div className="panel-header">Tasa marginal de pérdida de grasa (g/kg)</div>
            <HealthOverlayLineChart
              seriesList={[
                { series: fatRateSeries, color: '#ef4444', name: 'g grasa por kg peso' },
              ]}
              bands={[{ upper: fatRateBand.upper, lower: fatRateBand.lower, color: '#ef4444', opacity: 0.18 }]}
              height={220}
              valueSuffix=" g/kg"
              hoverId={sharedHoverIndex}
              onHoverIdChange={setSharedHoverIndex}
              yTickStep={100}
              renderTooltip={({ id, nearestIndexById }) => {
                const j = nearestIndexById(fatRateSeries || [], id);
                const pt = (fatRateSeries || [])[j];
                const k = nearestIndexById(fatRateBand.upper || [], id);
                const up = (fatRateBand.upper || [])[k];
                const lo = (fatRateBand.lower || [])[k];
                return (
                  <div>
                    <div style={{ marginBottom: 4, opacity: 0.9 }}>{formatDateShort(id)} (ventana 7d)</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 6 }}>
                      <div>Estimado:</div><div><strong>{Math.round(Number(pt?.value || 0))} g/kg</strong></div>
                      <div>IC95%:</div><div><strong>{Math.round(Number(lo?.value || 0))} – {Math.round(Number(up?.value || 0))} g/kg</strong></div>
                    </div>
                  </div>
                );
              }}
            />
          </div>
          
        </div>
      )}
    </div>
  );
}

export default HealthDashboardPage;


